import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Paper,
  Container,
  Tabs,
  Tab,
  IconButton,
  useMediaQuery,
  Chip,
  Dialog,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack,
  PlayArrow,
  Description,
  CheckCircle,
  ExpandMore,
  CheckCircleOutline,
  Launch,
  WorkspacePremium,
  SportsEsports,
  Lock,
  Star,
  StarBorder,
  Assignment,
  Error as ErrorIcon,
  CalendarToday,
  AccessTime,
  LinkedIn,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  getUserCourseContents,
  getTrilhaAcompanhamento,
  startContent,
  finishContent,
  saveVideoProgress,
  startModuleProgress,
  finishModuleProgress,
  finishCourseProgress,
  getCertificateTest,
  getFeedbackQuestoes,
  submitFeedbackAnswer,
  finishEvaluation,
  getAvaliacaoTentativas,
  getEvaluationQuestionsStatus,
  getConteudoUsuarioById,
  getContentProgress,
  getCourseContentsByModule,
  getUserCourseModules,
  getCourseModulesByVersion,
  getUserCourses,
  getCourses,
  getCertificateData,
} from '../services/api.js';
import { getCurrentUser } from '../utils/auth.js';

import ConsentDialog from '../components/ConsentDialog.js';
import Questionario from '../components/Questionario.js';

const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';

/** Normalize outline shape (slim may use `modulos` instead of `modulos_conteudos`). */
const normalizeOutlineData = data => {
  if (!data) return data;
  if (data.modulos_conteudos) return data;
  if (Array.isArray(data.modulos)) {
    return {
      ...data,
      modulos_conteudos: data.modulos.map(mod => ({
        id_curso_modulo_usuario: mod.id_curso_modulo_usuario,
        modulo_titulo: mod.titulo ?? mod.modulo_titulo,
        ordem: mod.ordem,
        concluido: mod.concluido,
        bloqueado: mod.bloqueado,
        feedback: mod.feedback,
        conteudos: (mod.conteudos || []).map(c => ({
          ...c,
          // Keep boolean flags if API only sends `tipo`
          simulador: c.simulador ?? c.tipo === 'simulador',
          avaliacao: c.avaliacao ?? c.tipo === 'avaliacao',
          atividade:
            c.atividade ??
            (c.tipo === 'atividade' || c.tipo === 'atividade_teorica'),
          is_feedback: c.is_feedback ?? c.tipo === 'feedback',
          questionario: c.questionario ?? c.tipo === 'questionario',
        })),
      })),
    };
  }
  return data;
};

const fetchOutlineWithSlimFallback = async courseUserId => {
  try {
    const slimData = await getUserCourseContents(courseUserId, {
      slim: true,
      limit: 100,
    });
    // Use response whether BE honors slim or ignores it (avoid double fetch).
    // When slim is active, payload is light; when ignored, same as full listagem.
    return normalizeOutlineData(slimData);
  } catch {
    const fullData = await getUserCourseContents(courseUserId, { limit: 100 });
    return normalizeOutlineData(fullData);
  }
};

const CursoConteudoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [videoTabIndex, setVideoTabIndex] = useState(0);
  const [curso, setCurso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aulaDetalhe, setAulaDetalhe] = useState(null);
  const [aulaDetalheLoading, setAulaDetalheLoading] = useState(false);
  const [aulaDetalheError, setAulaDetalheError] = useState(null);
  const [aulaDetalheBlocked, setAulaDetalheBlocked] = useState(false);
  const [moduloAtualIdx, setModuloAtualIdx] = useState(0);
  const [aulaAtualIdx, setAulaAtualIdx] = useState(0);
  const [progressoTotal, setProgressoTotal] = useState(0);
  // Resume/continuar pointer: filled from trilha on mount and from finish delta
  const [proximaAtividade, setProximaAtividade] = useState(null);
  const proximaAtividadeRef = useRef(null);
  proximaAtividadeRef.current = proximaAtividade;
  const [transicao, setTransicao] = useState(false);
  const [expandedModulo, setExpandedModulo] = useState(null);

  const [openCertDialog, setOpenCertDialog] = useState(false);
  const [certUrl, setCertUrl] = useState(null);
  const [certLoading, setCertLoading] = useState(false);

  // States para Avaliações
  const [respostas, setRespostas] = useState({});
  const [avaliacaoEnviada, setAvaliacaoEnviada] = useState(null);
  const [avaliacaoQuestoes, setAvaliacaoQuestoes] = useState([]);
  const [currentTentativaId, setCurrentTentativaId] = useState(null);
  const [avaliacaoMeta, setAvaliacaoMeta] = useState(null); // CursoAvaliacaoSchema: nota_corte, nr_tentativas_max
  const [avaliacaoTentativas, setAvaliacaoTentativas] = useState([]);
  const [avaliacaoLoading, setAvaliacaoLoading] = useState(true);
  const [questaoFeedbacks, setQuestaoFeedbacks] = useState({});
  // Pre-evaluation gate + countdown timer (1 hour = 3600 s)
  const [avaliacaoIniciada, setAvaliacaoIniciada] = useState(false);
  const [avaliacaoSecondsLeft, setAvaliacaoSecondsLeft] = useState(3600);
  const timerRef = useRef(null);

  // States para Feedback
  const [feedbackQuestoes, setFeedbackQuestoes] = useState([]);
  const [feedbackRespostas, setFeedbackRespostas] = useState({});
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [confirmSubmitAvaliacaoOpen, setConfirmSubmitAvaliacaoOpen] =
    useState(false);

  // State for video completion
  const [videoCompleted, setVideoCompleted] = useState(false);
  const aulaConteudoUsuarioIdRef = useRef(null);
  const aulaConcluidoRef = useRef(false);
  const lastSentVideoSecondsRef = useRef(null);
  const latestVideoSecondsRef = useRef(null);
  const videoProgressIntervalRef = useRef(null);
  const videoResumeSecondsRef = useRef(0);
  const videoSeekPendingRef = useRef(false);

  // States for Simulator Iframe
  const [showSimulatorIframe, setShowSimulatorIframe] = useState(false);
  const [simulatorUrl, setSimulatorUrl] = useState('');

  // States for Theoretical Activities

  // States for Consent Dialog
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingConsentAction, setPendingConsentAction] = useState(null);
  const [consentData, setConsentData] = useState({
    max: 3,
    remaining: 3,
    isSimulator: false,
    isUnlimited: false,
  });

  const location = useLocation();

  const moduloAtual = curso?.modulos_conteudos?.[moduloAtualIdx];
  const aulaOutline = moduloAtual?.conteudos?.[aulaAtualIdx];
  // Player uses detail when loaded; outline stubs drive sidebar/locks
  const aulaAtual = useMemo(
    () => (aulaDetalhe ? { ...aulaOutline, ...aulaDetalhe } : aulaOutline),
    [aulaOutline, aulaDetalhe]
  );

  const patchConteudoNoOutline = useCallback((conteudoUsuarioId, partial) => {
    setCurso(prev => {
      if (!prev?.modulos_conteudos) return prev;
      const modulos_conteudos = prev.modulos_conteudos.map(mod => ({
        ...mod,
        conteudos: (mod.conteudos || []).map(c =>
          c.id_curso_conteudo_usuario === conteudoUsuarioId
            ? { ...c, ...partial }
            : c
        ),
      }));
      return { ...prev, modulos_conteudos };
    });
    setAulaDetalhe(prev =>
      prev && prev.id_curso_conteudo_usuario === conteudoUsuarioId
        ? { ...prev, ...partial }
        : prev
    );
  }, []);

  /** Delta has proxima_atividade and is not a module reset — can skip full trilha for outline. */
  const hasProximaAtividadeDelta = result =>
    !!result?.proxima_atividade?.id_curso_conteudo_usuario &&
    !result?.modulo_zerado;

  /** Skip trilha entirely when proxima + progresso_geral are present. */
  const isDeltaSufficientToSkipTrilha = result =>
    hasProximaAtividadeDelta(result) &&
    typeof result?.progresso_geral === 'number';

  const applyProgressDeltaToCurso = useCallback(
    (cursoData, result, { conteudoUsuarioId } = {}) => {
      if (!cursoData?.modulos_conteudos) return cursoData;

      const finishedId =
        result?.conteudo?.id_curso_conteudo_usuario ?? conteudoUsuarioId;
      const liberadosMap = new Map(
        (result?.modulos_liberados || []).map(m => [
          m.id_curso_modulo_usuario,
          m.bloqueado,
        ])
      );
      const proximaId = result?.proxima_atividade?.id_curso_conteudo_usuario;

      return {
        ...cursoData,
        modulos_conteudos: cursoData.modulos_conteudos.map(mod => {
          const nextMod = { ...mod };
          if (liberadosMap.has(mod.id_curso_modulo_usuario)) {
            nextMod.bloqueado = liberadosMap.get(mod.id_curso_modulo_usuario);
          }
          nextMod.conteudos = (mod.conteudos || []).map(c => {
            let next = c;
            if (finishedId && c.id_curso_conteudo_usuario === finishedId) {
              next = {
                ...next,
                concluido: result?.conteudo?.concluido ?? true,
              };
            }
            if (proximaId && c.id_curso_conteudo_usuario === proximaId) {
              next = { ...next, bloqueado: false };
            }
            return next;
          });
          return nextMod;
        }),
      };
    },
    []
  );

  const applyProgressDelta = useCallback(
    (result, { conteudoUsuarioId, baseCurso } = {}) => {
      if (!result) return baseCurso ?? curso;

      if (typeof result.progresso_geral === 'number') {
        setProgressoTotal(Math.round(result.progresso_geral));
      }
      if (result.proxima_atividade) {
        setProximaAtividade(result.proxima_atividade);
      }

      const finishedId =
        result?.conteudo?.id_curso_conteudo_usuario ?? conteudoUsuarioId;
      const source = baseCurso ?? curso;
      const updated = applyProgressDeltaToCurso(source, result, {
        conteudoUsuarioId: finishedId,
      });

      if (updated) {
        setCurso(updated);
      }
      if (finishedId) {
        setAulaDetalhe(prev =>
          prev && prev.id_curso_conteudo_usuario === finishedId
            ? {
                ...prev,
                concluido: result?.conteudo?.concluido ?? true,
                ...(typeof result.tentativas_restantes === 'number'
                  ? { tentativas_restantes: result.tentativas_restantes }
                  : {}),
              }
            : prev
        );
      }

      return updated;
    },
    [curso, applyProgressDeltaToCurso]
  );

  const refreshProgressoFromTrilha = useCallback(async () => {
    try {
      const trilhaData = await getTrilhaAcompanhamento(id);
      setProgressoTotal(Math.round(trilhaData.progresso_geral || 0));
      if (trilhaData.proxima_atividade) {
        setProximaAtividade(trilhaData.proxima_atividade);
      }
      return trilhaData;
    } catch {
      return null;
    }
  }, [id]);

  const loadData = useCallback(
    async (shouldOpenFirstLesson = false) => {
      try {
        if (shouldOpenFirstLesson) setLoading(true);
        const [contentsData, trilhaData] = await Promise.all([
          fetchOutlineWithSlimFallback(id),
          getTrilhaAcompanhamento(id),
        ]);

        // Map id_curso_conteudo from trilhaData to contentsData.
        if (contentsData.modulos_conteudos) {
          const contentMap = {};
          (trilhaData.conteudo_path || []).forEach(c => {
            if (c.id_curso_conteudo_usuario && c.id_curso_conteudo) {
              contentMap[c.id_curso_conteudo_usuario] = c.id_curso_conteudo;
            }
          });
          if (
            trilhaData.proxima_atividade?.id_curso_conteudo_usuario &&
            trilhaData.proxima_atividade?.id_curso_conteudo
          ) {
            contentMap[trilhaData.proxima_atividade.id_curso_conteudo_usuario] =
              trilhaData.proxima_atividade.id_curso_conteudo;
          }

          contentsData.modulos_conteudos.forEach(modulo => {
            (modulo.conteudos || []).forEach(conteudo => {
              if (conteudo.id_curso_conteudo) return;
              const mappedId = contentMap[conteudo.id_curso_conteudo_usuario];
              if (mappedId) {
                conteudo.id_curso_conteudo = mappedId;
              } else if (conteudo.simulador) {
                console.warn(
                  '[CursoConteudoPage] id_curso_conteudo ausente para simulador:',
                  conteudo.id_curso_conteudo_usuario,
                  conteudo
                );
              }
            });
          });
        }

        setCurso(contentsData);
        setProgressoTotal(Math.round(trilhaData.progresso_geral || 0));
        if (trilhaData.proxima_atividade) {
          setProximaAtividade(trilhaData.proxima_atividade);
        }

        const findResumeFromFlat = cursoData => {
          const allMods = cursoData.modulos_conteudos || [];
          const flatC = allMods.flatMap(m => m.conteudos || []);
          let lastDoneIdx = -1;
          for (let i = 0; i < flatC.length; i++) {
            if (flatC[i].concluido) lastDoneIdx = i;
          }
          let resumeConteudo = null;
          if (lastDoneIdx !== -1) {
            resumeConteudo =
              lastDoneIdx + 1 < flatC.length
                ? flatC[lastDoneIdx + 1]
                : flatC[lastDoneIdx];
          } else {
            resumeConteudo = flatC.find(c => c.obrigatorio) || flatC[0];
          }
          const resumeModulo = resumeConteudo
            ? allMods.find(m =>
                m.conteudos?.some(
                  c =>
                    c.id_curso_conteudo_usuario ===
                    resumeConteudo.id_curso_conteudo_usuario
                )
              )
            : null;
          return { resumeModulo, resumeConteudo };
        };

        const handleResume = (cursoData, currentModulo, currentConteudo) => {
          if (currentModulo && currentConteudo) {
            const mIdx = cursoData.modulos_conteudos.findIndex(
              m =>
                m.id_curso_modulo_usuario ===
                currentModulo.id_curso_modulo_usuario
            );
            if (mIdx !== -1) {
              setModuloAtualIdx(mIdx);
              const aIdx = cursoData.modulos_conteudos[
                mIdx
              ].conteudos.findIndex(
                c =>
                  c.id_curso_conteudo_usuario ===
                  currentConteudo.id_curso_conteudo_usuario
              );
              if (aIdx !== -1) {
                setAulaAtualIdx(aIdx);
                if (!currentConteudo.concluido) {
                  startContent(currentConteudo.id_curso_conteudo_usuario).catch(
                    () => {}
                  );
                }
                if (aIdx === 0 && !currentModulo.concluido) {
                  startModuleProgress(
                    currentModulo.id_curso_modulo_usuario
                  ).catch(() => {});
                }
              }
            }
          }
        };

        const resumeFromProximaAtividade = () => {
          const proxima = trilhaData.proxima_atividade;
          if (!proxima?.id_curso_conteudo_usuario) return false;

          const allMods = contentsData.modulos_conteudos || [];
          let mIdx = -1;
          let aIdx = -1;
          if (proxima.modulo?.id_curso_modulo_usuario) {
            mIdx = allMods.findIndex(
              m =>
                m.id_curso_modulo_usuario ===
                proxima.modulo.id_curso_modulo_usuario
            );
          }
          if (mIdx === -1) {
            mIdx = allMods.findIndex(m =>
              m.conteudos?.some(
                c =>
                  c.id_curso_conteudo_usuario ===
                  proxima.id_curso_conteudo_usuario
              )
            );
          }
          if (mIdx === -1) return false;
          aIdx = (allMods[mIdx].conteudos || []).findIndex(
            c =>
              c.id_curso_conteudo_usuario === proxima.id_curso_conteudo_usuario
          );
          if (aIdx === -1) return false;

          handleResume(
            contentsData,
            allMods[mIdx],
            allMods[mIdx].conteudos[aIdx]
          );
          return true;
        };

        if (shouldOpenFirstLesson) {
          const queryParams = new URLSearchParams(location.search);
          const requestedModuleIdx = queryParams.get('module');

          if (requestedModuleIdx !== null) {
            const mIdx = parseInt(requestedModuleIdx, 10);
            if (!isNaN(mIdx) && contentsData.modulos_conteudos[mIdx]) {
              let isLocked = false;
              const modRef = contentsData.modulos_conteudos[mIdx];
              if (modRef.bloqueado === true) {
                isLocked = true;
              } else if (mIdx > 0) {
                const prevModulo = contentsData.modulos_conteudos[mIdx - 1];
                if (prevModulo.bloqueado === false && prevModulo.concluido) {
                  isLocked = false;
                } else {
                  const hasPendingMandatory = prevModulo.conteudos?.some(
                    c => c.obrigatorio && !c.concluido
                  );
                  if (hasPendingMandatory) isLocked = true;
                }
              }

              if (!isLocked) {
                setModuloAtualIdx(mIdx);
                setAulaAtualIdx(0);
                const moduleRef = contentsData.modulos_conteudos[mIdx];
                if (!moduleRef.concluido) {
                  startModuleProgress(moduleRef.id_curso_modulo_usuario).catch(
                    () => {}
                  );
                }
                const firstLesson = moduleRef.conteudos?.[0];
                if (firstLesson && !firstLesson.concluido) {
                  startContent(firstLesson.id_curso_conteudo_usuario).catch(
                    () => {}
                  );
                }
              } else {
                if (!resumeFromProximaAtividade()) {
                  const { resumeModulo, resumeConteudo } =
                    findResumeFromFlat(contentsData);
                  handleResume(contentsData, resumeModulo, resumeConteudo);
                }
              }
            }
          } else if (!resumeFromProximaAtividade()) {
            const { resumeModulo, resumeConteudo } =
              findResumeFromFlat(contentsData);
            handleResume(contentsData, resumeModulo, resumeConteudo);
          }
        }
        setLoading(false);
        return contentsData;
      } catch {
        setError('Erro ao carregar conteúdo do curso');
        setLoading(false);
        return null;
      }
    },
    [id, location.search]
  );

  useEffect(() => {
    loadData(true);
    window.scrollTo(0, 0);
  }, [loadData]);

  // Sync sidebar expansion with current module
  useEffect(() => {
    if (moduloAtualIdx !== null && moduloAtualIdx !== undefined) {
      setExpandedModulo(moduloAtualIdx);
    }
  }, [moduloAtualIdx]);

  // Reset feedback state when lesson changes
  useEffect(() => {
    setFeedbackSent(false);
    setVideoCompleted(false);
    setVideoTabIndex(0);
    setFeedbackRespostas({});

    // Reset evaluation states
    setAvaliacaoQuestoes([]);
    setAvaliacaoMeta(null);
    setAvaliacaoTentativas([]);
    setAvaliacaoEnviada(null);
    setRespostas({});
    setCurrentTentativaId(null);
    setQuestaoFeedbacks({});
    // Reset evaluation gate & timer
    setAvaliacaoIniciada(false);
    setAvaliacaoSecondsLeft(3600);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Reset simulator states
    setShowSimulatorIframe(false);
    setSimulatorUrl('');

    // Scroll back to the top of the page and content area when content changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [aulaAtualIdx, moduloAtualIdx]);

  // Load full lesson detail for the player (outline stays slim)
  useEffect(() => {
    const conteudoId = aulaOutline?.id_curso_conteudo_usuario;
    if (!conteudoId) {
      setAulaDetalhe(null);
      setAulaDetalheError(null);
      setAulaDetalheBlocked(false);
      setAulaDetalheLoading(false);
      return;
    }

    let cancelled = false;
    setAulaDetalhe(null);
    setAulaDetalheError(null);
    setAulaDetalheBlocked(false);
    setAulaDetalheLoading(true);

    getConteudoUsuarioById(conteudoId)
      .then(data => {
        if (cancelled) return;
        setAulaDetalhe(data);
        setAulaDetalheLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setAulaDetalhe(null);
        setAulaDetalheLoading(false);
        if (err.status === 403) {
          setAulaDetalheBlocked(true);
          setAulaDetalheError(
            'Este conteúdo ainda não está liberado. Conclua as etapas obrigatórias anteriores.'
          );
        } else {
          setAulaDetalheBlocked(false);
          setAulaDetalheError(
            err.message || 'Erro ao carregar o conteúdo da aula'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [aulaOutline?.id_curso_conteudo_usuario]);

  const fetchAvaliacaoData = useCallback(
    async (forceId = null) => {
      const isAvaliacao =
        aulaAtual?.avaliacao === true ||
        (aulaAtual?.atividade === true && aulaAtual?.atividades);

      if (!isAvaliacao || !aulaAtual) return;

      setAvaliacaoLoading(true);
      try {
        let questoes = aulaAtual.atividades || [];

        if (
          (!questoes || questoes.length === 0) &&
          (aulaAtual.id_curso_conteudo_usuario || aulaAtual.id_curso_conteudo)
        ) {
          try {
            const evalData = await getAvaliacaoByConteudo(
              aulaAtual.id_curso_conteudo_usuario || aulaAtual.id_curso_conteudo
            );
            if (evalData?.atividades) {
              questoes = evalData.atividades;
            } else if (Array.isArray(evalData)) {
              questoes = evalData;
            }
          } catch (e) {
            console.warn('Could not fetch avaliacao by conteudo:', e);
          }
        }

        setAvaliacaoQuestoes(questoes);

        // Initialize/Merge answers from activities (nm_resposta)
        const prefilledRespostas = {};
        questoes.forEach((questao, index) => {
          if (
            questao.nm_resposta !== null &&
            questao.nm_resposta !== undefined
          ) {
            if (questao.cd_tipo_questao === 'O') {
              prefilledRespostas[index] = String(questao.nm_resposta);
            } else {
              prefilledRespostas[index] = questao.nm_resposta;
            }
          }
        });

        setRespostas(prev => ({
          ...prefilledRespostas,
          ...prev,
        }));

        if (questoes.length > 0) {
          const firstQuestao = questoes[0];
          const tentativaId =
            currentTentativaId ||
            aulaAtual.id_curso_tentativa_usuario ||
            aulaAtual.id_curso_avaliacao_usuario ||
            firstQuestao.id_curso_tentativa_usuario ||
            firstQuestao.id_curso_avaliacao_usuario;

          if (tentativaId) {
            setCurrentTentativaId(tentativaId);
          }
        }

        // Fetch attempts history
        let attemptsArray = [];
        try {
          const attempts = await getAvaliacaoTentativas(
            aulaAtual.id_curso_conteudo_usuario || aulaAtual.id_curso_conteudo
          );
          attemptsArray = (attempts || []).sort(
            (a, b) =>
              (b.id_curso_tentativa_usuario || 0) -
              (a.id_curso_tentativa_usuario || 0)
          );
          setAvaliacaoTentativas(attemptsArray);

          // Extract question feedback directly from attempts array (e.g. /api/v1/curso-avaliacao-usuario/)
          if (attemptsArray && attemptsArray.length > 0) {
            const directFeedbackMap = {};
            let hasDirectFeedback = false;

            // Sort attempts descending by id_curso_avaliacao_usuario so the LATEST answer for each question is processed first
            const sortedByLatest = [...attemptsArray].sort((a, b) => {
              const idA = Number(a.id_curso_avaliacao_usuario) || 0;
              const idB = Number(b.id_curso_avaliacao_usuario) || 0;
              return idB - idA;
            });

            sortedByLatest.forEach(item => {
              const isCorrect =
                item.bo_ok !== undefined && item.bo_ok !== null
                  ? !!item.bo_ok
                  : item.nota !== undefined && item.nota !== null
                    ? Number(item.nota) > 0
                    : false;

              if (
                item.id_curso_avaliacao &&
                directFeedbackMap[item.id_curso_avaliacao] === undefined
              ) {
                directFeedbackMap[item.id_curso_avaliacao] = isCorrect;
                hasDirectFeedback = true;
              }
              if (
                item.id_curso_avaliacao_questao &&
                directFeedbackMap[item.id_curso_avaliacao_questao] === undefined
              ) {
                directFeedbackMap[item.id_curso_avaliacao_questao] = isCorrect;
                hasDirectFeedback = true;
              }
            });

            if (hasDirectFeedback) {
              setQuestaoFeedbacks(prev => ({ ...prev, ...directFeedbackMap }));
            }
          }
        } catch (e) {
          console.warn('Error loading attempts:', e);
        }

        const representativeId =
          (typeof forceId === 'number' || typeof forceId === 'string'
            ? forceId
            : null) ||
          attemptsArray[0]?.id_curso_tentativa_usuario ||
          currentTentativaId ||
          aulaAtual.id_curso_tentativa_usuario ||
          aulaAtual.id_curso_avaliacao_usuario;

        let enviadaSet = false;

        if (representativeId) {
          const representativeAttempt = attemptsArray.find(
            a => a.id_curso_tentativa_usuario === representativeId
          );
          const isFinished =
            !!forceId || representativeAttempt?.dh_fim || aulaAtual.concluido;

          if (isFinished) {
            try {
              let detailedResults = null;
              const maxRetries = forceId ? 4 : 1;

              for (let i = 0; i < maxRetries; i++) {
                try {
                  detailedResults = await getEvaluationQuestionsStatus({
                    id_curso_tentativa_usuario: representativeId,
                  });
                } catch (retryError) {
                  // Ignore errors during retry loop, as backend might return 404 temporarily while processing
                  console.warn(
                    `Attempt ${i + 1} to fetch evaluation results failed.`
                  );
                }

                if (detailedResults && detailedResults.length > 0) {
                  break;
                }

                if (forceId && i < maxRetries - 1) {
                  // Wait 1.5 seconds before retrying to allow backend to process the submission
                  await new Promise(resolve => setTimeout(resolve, 1500));
                }
              }

              if (detailedResults && detailedResults.length > 0) {
                let correctCount = 0;
                const totalCount = detailedResults.length;
                const feedbackMap = {};
                const resultPrefilled = {};

                detailedResults.forEach(item => {
                  const isCorrect =
                    item.bo_ok !== undefined && item.bo_ok !== null
                      ? !!item.bo_ok
                      : item.nota !== undefined && item.nota !== null
                        ? Number(item.nota) > 0
                        : false;

                  if (isCorrect) correctCount++;

                  if (item.id_curso_avaliacao) {
                    feedbackMap[item.id_curso_avaliacao] = isCorrect;
                  }
                  if (item.id_curso_avaliacao_questao) {
                    feedbackMap[item.id_curso_avaliacao_questao] = isCorrect;
                  }

                  const qIdx = questoes.findIndex(
                    q =>
                      (q.id_curso_avaliacao &&
                        q.id_curso_avaliacao === item.id_curso_avaliacao) ||
                      (q.id_curso_avaliacao_questao &&
                        q.id_curso_avaliacao_questao ===
                          item.id_curso_avaliacao_questao) ||
                      (q.id_curso_avaliacao &&
                        q.id_curso_avaliacao ===
                          item.id_curso_avaliacao_questao)
                  );

                  if (
                    qIdx !== -1 &&
                    item.id_curso_avaliacao_questao !== null &&
                    item.id_curso_avaliacao_questao !== undefined
                  ) {
                    resultPrefilled[qIdx] = item.id_curso_avaliacao_questao;
                  }
                });

                const derivedNota = (correctCount / totalCount) * 10;
                const notaCorte = aulaAtual.nota_corte || 7;

                const initialEnviada = {
                  bo_aprovado: derivedNota >= notaCorte || aulaAtual.concluido,
                  nota: derivedNota,
                  qt_corretas: correctCount,
                  qt_total: totalCount,
                  dh_fim:
                    detailedResults[0]?.dh_inclusao ||
                    representativeAttempt?.dh_fim ||
                    new Date().toISOString(),
                };

                setAvaliacaoEnviada(initialEnviada);
                setQuestaoFeedbacks(prev => ({ ...prev, ...feedbackMap }));
                setRespostas(prev => ({ ...resultPrefilled, ...prev }));
                enviadaSet = true;
              }
            } catch (err) {
              console.error('Error fetching evaluation questions status:', err);
            }
          }
        }

        // CRITICAL FALLBACK: If lesson is concluded OR attempt is finished, but avaliacaoEnviada was not set by detailedResults
        if (!enviadaSet && aulaAtual.concluido) {
          setAvaliacaoEnviada(prev => {
            if (prev) return prev;
            return {
              bo_aprovado: true,
              nota: aulaAtual.nota ?? 10,
              qt_corretas: aulaAtual.acertos ?? (questoes.length || 0),
              qt_total: questoes.length || 0,
            };
          });
        }
      } catch (err) {
        console.error('Erro em fetchAvaliacaoData:', err);
      } finally {
        setAvaliacaoLoading(false);
      }
    },
    [aulaAtual, currentTentativaId]
  );

  useEffect(() => {
    fetchAvaliacaoData();
  }, [fetchAvaliacaoData]);



  // Scroll to top of content when lesson changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo(0, 0);
    }
  }, [moduloAtualIdx, aulaAtualIdx]);

  // Scroll to top when course is concluded so certificate is visible
  useEffect(() => {
    if (curso?.concluido) {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [curso?.concluido]);

  // Scroll to top when evaluation is submitted so user sees results immediately
  useEffect(() => {
    if (avaliacaoEnviada) {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [avaliacaoEnviada]);

  // Listen for Vimeo messages
  useEffect(() => {
    const clearVideoProgressInterval = () => {
      if (videoProgressIntervalRef.current) {
        clearInterval(videoProgressIntervalRef.current);
        videoProgressIntervalRef.current = null;
      }
    };

    const requestVimeoCurrentTime = () => {
      const iframe = document.getElementById('vimeo-player');
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ method: 'getCurrentTime' }),
          '*'
        );
      }
    };

    const sendVideoProgress = seconds => {
      const contentId = aulaConteudoUsuarioIdRef.current;
      if (!contentId || aulaConcluidoRef.current) return;

      const tempo = Math.floor(Number(seconds));
      if (!Number.isFinite(tempo) || tempo < 0) return;

      // Ignore early 0s updates before setCurrentTime resumes playback
      if (videoSeekPendingRef.current) {
        if (tempo + 1 < videoResumeSecondsRef.current) return;
        videoSeekPendingRef.current = false;
      }

      if (lastSentVideoSecondsRef.current === tempo) return;

      lastSentVideoSecondsRef.current = tempo;
      saveVideoProgress(contentId, tempo).catch(() => {});
    };

    const startVideoProgressInterval = () => {
      clearVideoProgressInterval();
      videoProgressIntervalRef.current = setInterval(() => {
        requestVimeoCurrentTime();
        if (latestVideoSecondsRef.current != null) {
          sendVideoProgress(latestVideoSecondsRef.current);
        }
      }, 15000);
    };

    const handleVimeoMessage = event => {
      try {
        // Vimeo messages might be objects or JSON strings
        let data = event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch {
            return;
          }
        }

        // Response to getCurrentTime
        if (data?.method === 'getCurrentTime' && data.value != null) {
          latestVideoSecondsRef.current = data.value;
          sendVideoProgress(data.value);
          return;
        }

        if (data?.event === 'play') {
          if (data.data?.seconds != null) {
            latestVideoSecondsRef.current = data.data.seconds;
          }
          startVideoProgressInterval();
        } else if (data?.event === 'finish') {
          clearVideoProgressInterval();
          setVideoCompleted(true);
          // Do not PATCH on finish — avoids racing with POST finalizar conteúdo
          if (data.data?.seconds != null) {
            latestVideoSecondsRef.current = data.data.seconds;
          }
          aulaConcluidoRef.current = true;
        } else if (data?.event === 'pause') {
          clearVideoProgressInterval();
          if (data.data?.seconds != null) {
            latestVideoSecondsRef.current = data.data.seconds;
            sendVideoProgress(data.data.seconds);
          } else if (latestVideoSecondsRef.current != null) {
            sendVideoProgress(latestVideoSecondsRef.current);
          }
        } else if (data?.event === 'timeupdate') {
          // Keep latest seconds only; PATCH is driven by play interval / pause / finish
          if (data.data?.percent >= 0.95) {
            setVideoCompleted(true);
          }
          if (data.data?.seconds != null) {
            latestVideoSecondsRef.current = data.data.seconds;
          }
        } else if (data?.event === 'ready') {
          const iframe = document.getElementById('vimeo-player');
          if (iframe && iframe.contentWindow) {
            ['play', 'finish', 'timeupdate', 'pause'].forEach(value => {
              iframe.contentWindow.postMessage(
                JSON.stringify({ method: 'addEventListener', value }),
                '*'
              );
            });

            const resumeSeconds = videoResumeSecondsRef.current;
            if (resumeSeconds > 0) {
              videoSeekPendingRef.current = true;
              latestVideoSecondsRef.current = resumeSeconds;
              iframe.contentWindow.postMessage(
                JSON.stringify({
                  method: 'setCurrentTime',
                  value: resumeSeconds,
                }),
                '*'
              );
            }
          }
        }
      } catch {
        // Ignore errors from other iframes or separate sources
      }
    };

    window.addEventListener('message', handleVimeoMessage);
    return () => {
      clearVideoProgressInterval();
      window.removeEventListener('message', handleVimeoMessage);
    };
  }, []);

  // Keep video progress refs in sync with the current lesson
  useEffect(() => {
    const parseVimeoStartSeconds = url => {
      if (!url) return 0;
      const match = String(url).match(/#t=([^&#]*)/i);
      if (!match) return 0;
      const token = decodeURIComponent(match[1]);
      if (/^\d+(?:\.\d+)?s?$/i.test(token)) {
        return Math.floor(parseFloat(token));
      }
      let seconds = 0;
      const hours = token.match(/(\d+)h/i);
      const minutes = token.match(/(\d+)m/i);
      const secs = token.match(/(\d+(?:\.\d+)?)s/i);
      if (hours) seconds += parseInt(hours[1], 10) * 3600;
      if (minutes) seconds += parseInt(minutes[1], 10) * 60;
      if (secs) seconds += Math.floor(parseFloat(secs[1]));
      return seconds;
    };

    const resumeSeconds =
      parseVimeoStartSeconds(aulaAtual?.link) ||
      parseVimeoStartSeconds(aulaAtual?.video_info?.player_embed_url) ||
      parseVimeoStartSeconds(aulaAtual?.video_info?.embed_url) ||
      (Number(aulaAtual?.tempo_segundos) > 0
        ? Math.floor(Number(aulaAtual.tempo_segundos))
        : 0);

    aulaConteudoUsuarioIdRef.current =
      aulaAtual?.id_curso_conteudo_usuario ?? null;
    aulaConcluidoRef.current = !!aulaAtual?.concluido;
    videoResumeSecondsRef.current = resumeSeconds;
    videoSeekPendingRef.current = resumeSeconds > 0;
    latestVideoSecondsRef.current = resumeSeconds > 0 ? resumeSeconds : null;
    // Avoid immediately re-PATCHing the same resumed second
    lastSentVideoSecondsRef.current = resumeSeconds > 0 ? resumeSeconds : null;
    if (videoProgressIntervalRef.current) {
      clearInterval(videoProgressIntervalRef.current);
      videoProgressIntervalRef.current = null;
    }
  }, [
    aulaAtual?.id_curso_conteudo_usuario,
    aulaAtual?.concluido,
    aulaAtual?.link,
    aulaAtual?.video_info?.player_embed_url,
    aulaAtual?.video_info?.embed_url,
    aulaAtual?.tempo_segundos,
  ]);

  const checkAndFinishModuleOrCourse = useCallback(
    async currentCurso => {
      if (!currentCurso) return;

      const currentModulo = currentCurso.modulos_conteudos?.[moduloAtualIdx];
      if (!currentModulo) return;

      // Check if all MANDATORY contents in current module are complete
      // If there are no mandatory contents, all contents must be completed
      const hasMandatoryContent = currentModulo.conteudos?.some(
        c => c.obrigatorio
      );
      let allModuleContentsComplete = false;
      if (hasMandatoryContent) {
        allModuleContentsComplete = !currentModulo.conteudos?.some(
          c => c.obrigatorio && !c.concluido
        );
      } else {
        allModuleContentsComplete = !currentModulo.conteudos?.some(
          c => !c.concluido
        );
      }

      if (allModuleContentsComplete) {
        try {
          await finishModuleProgress(currentModulo.id_curso_modulo_usuario);
          // Manually update local state to reflect completion immediately
          // so that navigation logic sees it as completed
          currentModulo.concluido = true;
          // Notify React of the mutation
          setCurso({ ...currentCurso });
        } catch {
          // Ignore error or handle gracefully
        }

        // If this was the last module, check if all modules are complete
        const isLastModule =
          moduloAtualIdx === currentCurso.modulos_conteudos.length - 1;
        if (isLastModule) {
          const hasPendingMandatoryCourseModule =
            currentCurso.modulos_conteudos.some(m =>
              m.conteudos.some(c => c.obrigatorio && !c.concluido)
            );
          const allModulesComplete = !hasPendingMandatoryCourseModule;

          if (allModulesComplete) {
            await finishCourseProgress(id).catch(() => {});
            // Course finalize runs after content delta; refresh trilha so
            // progresso_geral becomes 100 and the certificate view can show.
            await refreshProgressoFromTrilha();
          }
        }
      }
    },
    [moduloAtualIdx, id, refreshProgressoFromTrilha]
  );
  const handleConsentConfirm = () => {
    setConsentOpen(false);
    if (pendingConsentAction) {
      pendingConsentAction();
    }
  };

  const requestConsent = (
    action,
    max,
    remaining,
    isSimulator = false,
    isUnlimited = false
  ) => {
    setPendingConsentAction(() => action);
    setConsentData({ max, remaining, isSimulator, isUnlimited });
    setConsentOpen(true);
  };

  const handleMarcarConcluida = async () => {
    if (!aulaAtual || aulaAtual.concluido) return;
    try {
      const conteudoId = aulaAtual.id_curso_conteudo_usuario;
      const result = await finishContent(conteudoId);
      const updatedCurso = applyProgressDelta(result, {
        conteudoUsuarioId: conteudoId,
      });

      if (!isDeltaSufficientToSkipTrilha(result)) {
        await refreshProgressoFromTrilha();
      }

      if (updatedCurso) {
        await checkAndFinishModuleOrCourse(updatedCurso);

        const tipo = getTipo(aulaAtual);
        if (
          tipo !== 'questionario' &&
          tipo !== 'avaliacao' &&
          tipo !== 'atividade_teorica'
        ) {
          handleProximaAula(updatedCurso);
        }
      }
    } catch {
      // Erro ao finalizar aula
    }
  };

  const handleReiniciarModulo = async () => {
    setAvaliacaoEnviada(null);
    setRespostas({});
    setQuestaoFeedbacks({});
    setAvaliacaoIniciada(false);
    setAvaliacaoSecondsLeft(3600);
    setCurrentTentativaId(null);
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const updatedData = await loadData();
    if (updatedData) {
      mudarAula(moduloAtualIdx, 0, updatedData);
    }
  };

  const isModuloAcessivel = useCallback(
    (mIdx, cursoData = null) => {
      const targetCurso = cursoData || curso;
      if (mIdx === 0) return true;

      const mod = targetCurso?.modulos_conteudos?.[mIdx];
      if (mod && typeof mod.bloqueado === 'boolean') {
        return !mod.bloqueado;
      }

      const prevModulo = targetCurso?.modulos_conteudos?.[mIdx - 1];
      if (!prevModulo) return false;

      const hasPendingMandatory = prevModulo.conteudos?.some(
        c => c.obrigatorio && !c.concluido
      );
      return !hasPendingMandatory;
    },
    [curso]
  );

  const isConteudoAcessivel = useCallback(
    (mIdx, aIdx, cursoData = null) => {
      const targetCurso = cursoData || curso;
      if (!isModuloAcessivel(mIdx, targetCurso)) return false;

      const conteudosM = targetCurso?.modulos_conteudos?.[mIdx]?.conteudos;
      if (!conteudosM) return false;

      const target = conteudosM[aIdx];
      if (target && typeof target.bloqueado === 'boolean') {
        return !target.bloqueado;
      }

      if (aIdx === 0) return true;

      for (let i = 0; i < aIdx; i++) {
        const c = conteudosM[i];
        if (c.obrigatorio && !c.concluido) {
          return false;
        }
      }
      return true;
    },
    [curso, isModuloAcessivel]
  );

  const mudarAula = (mIdx, aIdx, cursoData = null) => {
    if (!isConteudoAcessivel(mIdx, aIdx, cursoData)) return;

    setTransicao(true);
    setRespostas({});
    setAvaliacaoEnviada(null);
    window.setTimeout(() => {
      setModuloAtualIdx(mIdx);
      setAulaAtualIdx(aIdx);
      setTransicao(false);

      const targetCurso = cursoData || curso;
      const newAula = targetCurso?.modulos_conteudos?.[mIdx]?.conteudos?.[aIdx];

      if (newAula) {
        if (!newAula.concluido) {
          startContent(newAula.id_curso_conteudo_usuario).catch(() => {});
        }

        // Refined Logic: If it's the first content of the module, start the module
        if (aIdx === 0) {
          const modulo = targetCurso?.modulos_conteudos?.[mIdx];
          if (modulo && !modulo.concluido) {
            startModuleProgress(modulo.id_curso_modulo_usuario).catch(() => {});
          }
        }
      }
    }, 300);
  };

  const handleProximaAula = (cursoData = null) => {
    const targetCurso = cursoData || curso;
    if (!targetCurso) return;

    // We need to re-derive moduloAtual/aulaAtual from indices for the *target* data if provided
    // BUT indices (moduloAtualIdx) state might be stale if we relied on state.
    // However, we normally change idx only when moving.
    // Here we are determining WHERE to go based on CURRENT position.
    // The current position indices (moduloAtualIdx, aulaAtualIdx) are correct for *where we are now*.

    // Let's get the specific module from the fresh data using the CURRENT index
    const currentModuloFromData =
      targetCurso.modulos_conteudos?.[moduloAtualIdx];

    if (!currentModuloFromData) return;

    if (aulaAtualIdx < (currentModuloFromData.conteudos?.length || 0) - 1) {
      mudarAula(moduloAtualIdx, aulaAtualIdx + 1, targetCurso);
    } else if (
      moduloAtualIdx < targetCurso.modulos_conteudos.length - 1 &&
      isModuloAcessivel(moduloAtualIdx + 1, targetCurso)
    ) {
      mudarAula(moduloAtualIdx + 1, 0, targetCurso);
    }
  };

  const handleAulaAnterior = () => {
    if (aulaAtualIdx > 0) {
      mudarAula(moduloAtualIdx, aulaAtualIdx - 1);
    } else if (moduloAtualIdx > 0) {
      const prevModulo = curso.modulos_conteudos[moduloAtualIdx - 1];
      mudarAula(moduloAtualIdx - 1, prevModulo.conteudos.length - 1);
    }
  };

  const handleEnviarAvaliacaoRef = useRef(null);
  useEffect(() => {
    handleEnviarAvaliacaoRef.current = handleEnviarAvaliacao;
  });
  const avaliacaoEnviadaRef = useRef(avaliacaoEnviada);
  useEffect(() => {
    avaliacaoEnviadaRef.current = avaliacaoEnviada;
  }, [avaliacaoEnviada]);

  // Countdown timer: starts when evaluation is initiated and stops when finished/submitted
  useEffect(() => {
    if (!avaliacaoIniciada || avaliacaoEnviada) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setAvaliacaoSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          // Auto-submit when time runs out
          if (
            !avaliacaoEnviadaRef.current &&
            handleEnviarAvaliacaoRef.current
          ) {
            handleEnviarAvaliacaoRef.current();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [avaliacaoIniciada, avaliacaoEnviada]);

  // Scroll to top of content when lesson changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo(0, 0);
    }
  }, [moduloAtualIdx, aulaAtualIdx]);

  // Listen for iframe messages (Vimeo & StreamPixel / Simulator completion events)
  useEffect(() => {
    const handleIframeMessage = event => {
      try {
        let data = event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch {
            // String message (e.g. "finish", "completed", "simulation_completed")
            const strData = String(data).toLowerCase();
            if (
              strData === 'finish' ||
              strData === 'finished' ||
              strData === 'completed' ||
              strData === 'simulation_completed' ||
              strData === 'simulador_concluido'
            ) {
              handleMarcarConcluida();
            }
            return;
          }
        }

        if (data && typeof data === 'object') {
          const eventType = String(
            data.event ||
              data.type ||
              data.action ||
              data.status ||
              data.message ||
              ''
          ).toLowerCase();

          const isFinishEvent =
            eventType === 'finish' ||
            eventType === 'finished' ||
            eventType === 'complete' ||
            eventType === 'completed' ||
            eventType === 'simulation_end' ||
            eventType === 'simulation_completed' ||
            eventType === 'simulador_concluido' ||
            eventType === 'quiz_completed' ||
            data.completed === true ||
            data.concluido === true;

          if (isFinishEvent) {
            handleMarcarConcluida();
          }
        }
      } catch (err) {
        console.error('Error processing iframe message:', err);
      }
    };

    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [handleMarcarConcluida]);

  // Active polling for Simulator backend status changes (when iframe makes an HTTP request directly to the API)
  useEffect(() => {
    const isSimulador =
      aulaAtual?.simulador === true || aulaAtual?.tipo === 'simulador';
    const isConcluido = aulaAtual?.concluido;
    const conteudoId = aulaAtual?.id_curso_conteudo_usuario;

    if (!isSimulador || !conteudoId) {
      return;
    }

    let isSubscribed = true;

    const wasAlreadyCompleted = isConcluido;
    const initialTentativas = aulaAtual?.tentativas_restantes;

    const intervalId = setInterval(async () => {
      try {
        let freshConteudo = null;
        try {
          freshConteudo = await getConteudoUsuarioById(conteudoId);
        } catch (detailErr) {
          // Fallback to lightweight progress endpoint
          const progress = await getContentProgress(conteudoId);
          freshConteudo = {
            concluido: progress?.concluido,
            tentativas_restantes: progress?.tentativas_restantes,
          };
          if (detailErr.status === 403) return;
        }
        if (!isSubscribed) return;

        if (freshConteudo?.concluido) {
          const newTentativas = freshConteudo.tentativas_restantes;

          let shouldAdvance = false;
          if (!wasAlreadyCompleted) {
            shouldAdvance = true;
          } else if (
            newTentativas !== undefined &&
            initialTentativas !== undefined &&
            newTentativas !== initialTentativas
          ) {
            shouldAdvance = true;
          }

          if (shouldAdvance) {
            clearInterval(intervalId);
            patchConteudoNoOutline(conteudoId, {
              concluido: true,
              tentativas_restantes: newTentativas,
            });
            handleMarcarConcluida();
          }
        }
      } catch (err) {
        console.warn('Error polling simulator status:', err);
      }
    }, 3500);

    return () => {
      isSubscribed = false;
      clearInterval(intervalId);
    };
  }, [
    aulaAtual?.id_curso_conteudo_usuario,
    aulaAtual?.simulador,
    aulaAtual?.tipo,
    aulaAtual?.concluido,
    aulaAtual?.tentativas_restantes,
    handleMarcarConcluida,
    patchConteudoNoOutline,
  ]);

  // Atualizar `isLastAula`
  useEffect(() => {
    if (!curso?.modulos) return;
    const isUltimoModulo = moduloAtualIdx === curso.modulos.length - 1;
    const isUltimaAulaDoModulo =
      aulaAtualIdx === curso.modulos[moduloAtualIdx].aulas.length - 1;
    setIsLastAula(isUltimoModulo && isUltimaAulaDoModulo);
  }, [curso, moduloAtualIdx, aulaAtualIdx]);

  // Helper para formatar tempo (ex: "45 min" ou "1 h 30 min")
  const formatTime = minutes => {
    if (!minutes) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours} h ${mins} min` : `${hours} h`;
  };

  const handleNext = () => {
    handleProximaAula();
  };

  const handlePrev = () => {
    handleAulaAnterior();
  };

  const handleRespostaChange = (questaoIndex, value) => {
    setRespostas(prev => ({
      ...prev,
      [questaoIndex]: value,
    }));
  };

  const handleEnviarAvaliacao = async () => {
    if (!aulaAtual || avaliacaoQuestoes.length === 0) return;

    try {
      setAvaliacaoLoading(true);

      const tentativaId =
        currentTentativaId ||
        aulaAtual?.id_curso_tentativa_usuario ||
        aulaAtual?.id_curso_avaliacao_usuario ||
        avaliacaoQuestoes[0]?.id_curso_tentativa_usuario ||
        avaliacaoQuestoes[0]?.id_curso_avaliacao_usuario;

      if (!tentativaId) {
        throw new Error('ID da tentativa não encontrado.');
      }

      setCurrentTentativaId(tentativaId);

      // Preparar respostas
      const payload = {
        id_curso_tentativa_usuario: tentativaId,
        respostas: avaliacaoQuestoes.map((questao, index) => {
          const valor = respostas[index];
          const isObjective = questao.cd_tipo_questao === 'O';

          if (isObjective) {
            // According to finalized user requirements:
            // payload curso_avaliacao_usuario_id = questao.id_curso_avaliacao_usuario
            // payload id_curso_avaliacao_questao = selected Alternative ID (valor)
            return {
              id_curso_avaliacao_questao: valor ? parseInt(valor, 10) : null,
              curso_avaliacao_usuario_id: questao.id_curso_avaliacao_usuario,
            };
          } else {
            // Dissertative questions
            return {
              id_curso_avaliacao_questao: questao.id_curso_avaliacao_questao,
              nm_resposta: valor ? String(valor) : '',
            };
          }
        }),
      };

      const result = await finishEvaluation(payload);

      // Mapear resultado para manter compatibilidade com UI
      const mappedResult = {
        ...result,
        bo_aprovado: result.passou,
        nota: result.media,
        qt_corretas: result.acertos ?? result.qt_corretas,
        qt_total:
          result.total ??
          result.qt_total ??
          (avaliacaoQuestoes?.length || aulaAtual?.atividades?.length || 0),
      };

      setAvaliacaoEnviada(mappedResult);

      if (result.passou) {
        // Fetch results to get the derived stats using the true attempt ID fetched from backend
        await fetchAvaliacaoData(true);

        const conteudoId = aulaAtual?.id_curso_conteudo_usuario;
        const updatedFromEval = applyProgressDelta(result, {
          conteudoUsuarioId: conteudoId,
        });

        if (!isDeltaSufficientToSkipTrilha(result)) {
          await refreshProgressoFromTrilha();
        }

        // Avoid duplicate finishContent when evaluation delta already marked content done
        if (result.conteudo?.concluido === true) {
          if (updatedFromEval) {
            await checkAndFinishModuleOrCourse(updatedFromEval);
          }
        } else if (conteudoId && !aulaAtual?.concluido) {
          const finishResult = await finishContent(conteudoId);
          const updatedFromFinish = applyProgressDelta(finishResult, {
            conteudoUsuarioId: conteudoId,
            baseCurso: updatedFromEval,
          });
          if (!isDeltaSufficientToSkipTrilha(finishResult)) {
            await refreshProgressoFromTrilha();
          }
          if (updatedFromFinish) {
            await checkAndFinishModuleOrCourse(updatedFromFinish);
          }
        } else if (updatedFromEval) {
          await checkAndFinishModuleOrCourse(updatedFromEval);
        }
      } else {
        // Se não passou, atualizar o ID da tentativa para a próxima submissão se retornado
        if (result.id_curso_tentativa_usuario) {
          setCurrentTentativaId(result.id_curso_tentativa_usuario);
        }

        if (typeof result.progresso_geral === 'number') {
          setProgressoTotal(Math.round(result.progresso_geral));
        }
        if (
          aulaAtual?.id_curso_conteudo_usuario &&
          typeof result.tentativas_restantes === 'number'
        ) {
          patchConteudoNoOutline(aulaAtual.id_curso_conteudo_usuario, {
            tentativas_restantes: result.tentativas_restantes,
          });
        }

        await fetchAvaliacaoData(true);

        // If the backend indicates the module was reset (e.g., ran out of tries)
        if (result.modulo_zerado) {
          await handleReiniciarModulo();
          return;
        }

        // Fetch results to refresh the attempts counter and show feedback
        await fetchAvaliacaoData(tentativaId);
      }

      // Scroll to top of content container smoothly so user sees evaluation results
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } catch (err) {
      setError(err.message || 'Erro ao enviar avaliação. Tente novamente.');
    } finally {
      setAvaliacaoLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const user = getCurrentUser();
      const userId = user?.id_usuario;
      const courseId = id;

      if (!userId || !courseId) {
        return;
      }

      const userCourses = await getUserCourses();
      const enrolledCourses = userCourses?.cursos_usuario || userCourses || [];
      const currentUserCourse = Array.isArray(enrolledCourses)
        ? enrolledCourses.find(
            course => String(course.id_curso_usuario) === String(courseId)
          )
        : null;
      const courseVersionId =
        currentUserCourse?.id_curso_versao ||
        currentUserCourse?.curso?.id_curso_versao ||
        null;

      const blob = await getCertificateTest(userId, courseId, courseVersionId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'certificado.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // Erro ao baixar certificado
    }
  };

  const handleViewCertificate = async () => {
    try {
      setCertLoading(true);

      const user = getCurrentUser();
      const userId = user?.id_usuario;
      const courseId = id;

      if (!userId || !courseId) {
        setCertLoading(false);
        return;
      }

      const data = await getCertificateData(userId, courseId);
      let validationCode = data?.validation_code;
      if (!validationCode && Array.isArray(data) && data.length > 0) {
        validationCode = data[0]?.validation_code;
      } else if (
        !validationCode &&
        data?.results &&
        Array.isArray(data.results) &&
        data.results.length > 0
      ) {
        validationCode = data.results[0]?.validation_code;
      }

      if (validationCode) {
        const certificateUrl = `${window.location.origin}/certificados/validar/${validationCode}`;
        window.open(certificateUrl, '_blank');
      } else {
        // Fallback action or error notification
      }
    } catch {
      // Erro ao gerar link do certificado
    } finally {
      setCertLoading(false);
    }
  };

  const handleCloseCertDialog = () => {
    setOpenCertDialog(false);
    if (certUrl) {
      window.URL.revokeObjectURL(certUrl);
      setCertUrl(null);
    }
  };

  const handleShareLinkedIn = async () => {
    const user = getCurrentUser();
    const userId = user?.id_usuario || user?.id;
    const courseId = id;
    const courseTitle = curso?.titulo || curso?.nm_titulo || 'Curso';

    if (!userId || !courseId) {
      return;
    }

    try {
      const data = await getCertificateData(userId, courseId);
      let validationCode = data?.validation_code;
      if (!validationCode && Array.isArray(data) && data.length > 0) {
        validationCode = data[0]?.validation_code;
      } else if (
        !validationCode &&
        data?.results &&
        Array.isArray(data.results) &&
        data.results.length > 0
      ) {
        validationCode = data.results[0]?.validation_code;
      }

      if (!validationCode) return;

      const certificateUrl = `${window.location.origin}/certificados/validar/${validationCode}`;

      const postText = `🎓 Concluí o curso Operação e Manutenção de Reatores Fischer–Tropsch, que integra o projeto Produção de Combustível Sustentável de Aviação (SAF) a partir de Resíduos de Biomassa.\n\nA formação combinou conteúdos técnicos e atividades práticas em um simulador imersivo, proporcionando uma experiência prática em operação, manutenção, segurança e monitoramento das condições operacionais de reatores Fischer-Tropsch.\n\nO curso integra uma iniciativa realizada em parceria pela Geo Biogas & Carbon e pela Deutsche Gesellschaft für Internationale Zusammenarbeit (GIZ), com desenvolvimento e estruturação sob responsabilidade da 4Control. O projeto conta com o apoio da Finep e é implementado com recursos do programa develoPPP, do Ministério Federal da Cooperação Econômica e do Desenvolvimento da Alemanha (BMZ).\n\nConfira meu certificado aqui: ${certificateUrl}\n\n#FischerTropsch #SAF #OperaçãoIndustrial #CombustíveisSustentáveis`;

      const shareUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(
        postText
      ).replace(/%26/g, '%2526')}`;
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Erro ao gerar link do LinkedIn', err);
    }
  };

  const handleEnviarFeedback = async () => {
    try {
      setLoading(true);
      const user = getCurrentUser();

      if (!user?.id_usuario) {
        setError('Erro: Usuário não identificado.');
        setLoading(false);
        return;
      }

      // Envia cada resposta de feedback preenchida
      for (const questao of feedbackQuestoes) {
        const resposta = feedbackRespostas[questao.id_curso_feed];
        const isDissertative =
          questao.cd_tipo_questao === 'D' ||
          !questao.alternativas ||
          questao.alternativas.length === 0;

        if (resposta || isDissertative) {
          const payload = {
            id_curso_conteudo_usuario:
              aulaAtual?.id_curso_conteudo_usuario || 0,
            id_curso_feed: questao.id_curso_feed || 0,
          };

          if (isDissertative) {
            payload.nm_resposta = String(resposta || '');
          } else {
            payload.id_curso_questionario_questao = parseInt(
              resposta || '0',
              10
            );
          }
          await submitFeedbackAnswer(payload);
        }
      }

      setFeedbackSent(true);

      // Mark content as finished if it is a content item
      if (aulaAtual?.id_curso_conteudo_usuario && !aulaAtual.concluido) {
        const conteudoId = aulaAtual.id_curso_conteudo_usuario;
        const result = await finishContent(conteudoId);
        const updatedCurso = applyProgressDelta(result, {
          conteudoUsuarioId: conteudoId,
        });
        if (!isDeltaSufficientToSkipTrilha(result)) {
          await refreshProgressoFromTrilha();
        }
        if (updatedCurso) {
          await checkAndFinishModuleOrCourse(updatedCurso);
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (err) {
      setError('Erro ao enviar feedback: ' + (err.message || 'Desconhecido'));
      setLoading(false);
    }
  };

  const getTipo = aula => {
    if (!aula) return 'video';
    if (aula.tipo && typeof aula.tipo === 'string') {
      if (aula.tipo === 'atividade_teorica') return 'atividade_teorica';
      if (
        [
          'video',
          'avaliacao',
          'atividade',
          'simulador',
          'feedback',
          'questionario',
        ].includes(aula.tipo)
      ) {
        return aula.tipo;
      }
    }
    if (aula.questionario === true) return 'questionario';
    if (aula.simulador === true) return 'simulador';
    if (aula.is_feedback === true) return 'feedback';
    if (aula.avaliacao === true) return 'avaliacao';
    if (aula.atividade === true && aula.atividades) return 'atividade_teorica';
    if (aula.atividade === true) return 'atividade';
    if (aula.video_info) return 'video';
    return 'video';
  };

  const currentTipo = getTipo(aulaAtual);

  useEffect(() => {
    const loadFeedbackQuestions = async () => {
      if (aulaAtual && currentTipo === 'feedback') {
        try {
          const idRef =
            aulaAtual.id_curso_conteudo_usuario || aulaAtual.id_curso_conteudo;
          if (idRef) {
            const questoes = await getFeedbackQuestoes(idRef);
            setFeedbackQuestoes(questoes || []);
          }
        } catch {
          // Ignore error loading feedback questions
          setFeedbackQuestoes([]);
        }
      }
    };
    loadFeedbackQuestions();
  }, [aulaAtual, currentTipo]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#f8fafc',
        }}
      >
        <CircularProgress color='primary' />
      </Box>
    );
  }

  if (error || !curso) {
    return (
      <Container maxWidth='sm' sx={{ mt: 8 }}>
        <Alert severity='error'>{error || 'Curso não encontrado'}</Alert>
        <Button
          fullWidth
          variant='contained'
          startIcon={<ArrowBack />}
          onClick={() => navigate('/cursos')}
          sx={{ mt: 2 }}
        >
          Voltar para Meus Cursos
        </Button>
      </Container>
    );
  }

  const isLastModule =
    moduloAtualIdx === (curso?.modulos_conteudos?.length || 0) - 1;
  const isLastAula =
    isLastModule && aulaAtualIdx === (moduloAtual?.conteudos?.length || 0) - 1;
  const showCertificateView = progressoTotal === 100 && isLastAula;

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        height: '100%',
        bgcolor: '#f8fafc',
        overflow: 'hidden',
        position: 'relative',
        pt: { xs: '56px', sm: '64px' },
      }}
    >
      {/* Mobile sidebar toggle */}
      {isMobile && (
        <IconButton
          onClick={() => setSidebarOpen(!sidebarOpen)}
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 1300,
            bgcolor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          {sidebarOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      )}

      {/* Sidebar Modos/Aulas */}
      <Box
        sx={{
          width: { xs: '85vw', md: '25%' },
          minWidth: { md: 320 },
          maxWidth: { md: 420 },
          position: { xs: 'fixed', md: 'relative' },
          top: 0,
          bottom: 0,
          left: 0,
          bgcolor: '#FAFAFA',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          zIndex: { xs: 1200, md: 10 },
          boxShadow: {
            xs: '4px 0 24px rgba(0,0,0,0.15)',
            md: '4px 0 12px rgba(0,0,0,0.03)',
          },
          transform: {
            xs: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
            md: 'translateX(0)',
          },
          transition: 'transform 0.3s ease-in-out',
          height: { xs: '100vh', md: 'auto' },
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/cursos`)}
            sx={{ mb: 2, color: 'text.secondary', textTransform: 'none' }}
          >
            Voltar aos cursos
          </Button>
          <Typography variant='h6' fontWeight='bold' noWrap gutterBottom>
            Conteúdo do Curso
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
            >
              <Typography variant='caption' fontWeight='bold' color='primary'>
                {progressoTotal}% CONCLUÍDO
              </Typography>
            </Box>
            <LinearProgress
              variant='determinate'
              value={progressoTotal}
              sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9' }}
            />
            {progressoTotal === 100 && (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}
              >
                <Button
                  fullWidth
                  variant='contained'
                  color='success'
                  startIcon={<WorkspacePremium />}
                  onClick={handleDownloadCertificate}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)',
                  }}
                >
                  Download Certificado
                </Button>
                <Button
                  fullWidth
                  variant='contained'
                  startIcon={<LinkedIn />}
                  onClick={handleShareLinkedIn}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    bgcolor: '#0077b5',
                    boxShadow: '0 4px 12px rgba(0, 119, 181, 0.2)',
                    '&:hover': {
                      bgcolor: '#005582',
                    },
                  }}
                >
                  Compartilhar no LinkedIn
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          {curso.modulos_conteudos.map((mod, mIdx) => (
            <Accordion
              key={mod.id_curso_modulo_usuario}
              elevation={0}
              disableGutters
              expanded={expandedModulo === mIdx}
              onChange={(event, isExpanded) => {
                setExpandedModulo(isExpanded ? mIdx : null);
              }}
              disabled={!isModuloAcessivel(mIdx)}
              TransitionProps={{ unmountOnExit: true }}
              sx={{
                '&:before': { display: 'none' },
                borderBottom: '1px solid',
                borderColor: '#f1f5f9',
                opacity: isModuloAcessivel(mIdx) ? 1 : 0.7,
              }}
            >
              <AccordionSummary
                expandIcon={isModuloAcessivel(mIdx) ? <ExpandMore /> : <Lock />}
                sx={{
                  bgcolor: 'transparent',
                  '&.Mui-expanded': { bgcolor: '#F0F4F8' },
                  minHeight: 64,
                }}
              >
                <ListItemText
                  primary={`Módulo ${mod.ordem}`}
                  secondary={mod.modulo_titulo}
                  primaryTypographyProps={{
                    variant: 'caption',
                    fontWeight: 'bold',
                    color: isModuloAcessivel(mIdx)
                      ? 'primary'
                      : 'text.disabled',
                  }}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 'bold',
                    color: 'text.primary',
                    noWrap: true,
                  }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pb: 1 }}>
                <List disablePadding>
                  {mod.conteudos.map((aula, aIdx) => {
                    const isSelected =
                      mIdx === moduloAtualIdx && aIdx === aulaAtualIdx;
                    const acessivel = isConteudoAcessivel(mIdx, aIdx);
                    const type = getTipo(aula);

                    // Badge config per content type
                    const badgeConfig = {
                      video: {
                        label: 'Vídeo-aula',
                        bgcolor: '#E8F5E9',
                        color: '#2E7D32',
                      },
                      avaliacao: {
                        label: 'Avaliação',
                        bgcolor: '#FFF3E0',
                        color: '#E65100',
                      },
                      atividade: {
                        label: 'Atividade',
                        bgcolor: '#E3F2FD',
                        color: '#1565C0',
                      },
                      atividade_teorica: {
                        label: 'Atividade',
                        bgcolor: '#E3F2FD',
                        color: '#1565C0',
                      },
                      simulador: {
                        label: 'Simulador',
                        bgcolor: '#F3E5F5',
                        color: '#7B1FA2',
                      },
                      feedback: {
                        label: 'Feedback',
                        bgcolor: '#E0F7FA',
                        color: '#00695C',
                      },
                      questionario: {
                        label: 'Questionário',
                        bgcolor: '#FFF8E1',
                        color: '#F57F17',
                      },
                    };
                    const badge = badgeConfig[type] || {
                      label: 'Material',
                      bgcolor: '#F5F5F5',
                      color: '#616161',
                    };

                    // Icon per type
                    const iconSx = {
                      color: isSelected ? 'primary.main' : '#94a3b8',
                      fontSize: 22,
                    };
                    let typeIcon;
                    if (!acessivel) {
                      typeIcon = (
                        <Lock sx={{ fontSize: 22, color: 'text.disabled' }} />
                      );
                    } else if (aula.concluido) {
                      typeIcon = (
                        <CheckCircle sx={{ color: '#22c55e', fontSize: 22 }} />
                      );
                    } else if (type === 'video') {
                      typeIcon = <PlayArrow sx={iconSx} />;
                    } else if (
                      type === 'avaliacao' ||
                      type === 'atividade' ||
                      type === 'atividade_teorica' ||
                      type === 'questionario'
                    ) {
                      typeIcon = <Assignment sx={iconSx} />;
                    } else if (type === 'simulador') {
                      typeIcon = <SportsEsports sx={iconSx} />;
                    } else if (type === 'feedback') {
                      typeIcon = <Star sx={iconSx} />;
                    } else {
                      typeIcon = <Description sx={iconSx} />;
                    }

                    return (
                      <ListItem
                        key={aula.id_curso_conteudo_usuario}
                        button
                        disabled={!acessivel}
                        onClick={() => acessivel && mudarAula(mIdx, aIdx)}
                        sx={{
                          mx: 1.5,
                          mb: 0.5,
                          pl: 1.5,
                          pr: 1.5,
                          py: 1.5,
                          borderRadius: '12px',
                          bgcolor: isSelected ? '#FFFFFF' : 'transparent',
                          boxShadow: isSelected
                            ? '0 4px 14px rgba(0,0,0,0.04)'
                            : 'none',
                          border: isSelected
                            ? '1px solid rgba(0,0,0,0.04)'
                            : '1px solid transparent',
                          opacity: acessivel ? 1 : 0.6,
                          cursor: acessivel ? 'pointer' : 'not-allowed',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor:
                              acessivel && !isSelected
                                ? '#F0F4F8'
                                : isSelected
                                  ? '#FFFFFF'
                                  : 'transparent',
                          },
                        }}
                      >
                        {/* Icon */}
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {typeIcon}
                        </ListItemIcon>

                        {/* Number prefix */}
                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: 600,
                            color: isSelected ? 'primary.main' : '#94a3b8',
                            mr: 1,
                            flexShrink: 0,
                            fontSize: '0.85rem',
                          }}
                        >
                          {mod.ordem}.{aIdx + 1}
                        </Typography>

                        {/* Title + Badge */}
                        <Box
                          sx={{
                            flexGrow: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: isSelected ? 700 : 500,
                              color: isSelected
                                ? 'primary.main'
                                : 'text.primary',
                              lineHeight: 1.4,
                            }}
                          >
                            {aula.titulo}
                          </Typography>
                          <Chip
                            label={badge.label}
                            size='small'
                            sx={{
                              height: 22,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: badge.bgcolor,
                              color: badge.color,
                              borderRadius: '6px',
                              alignSelf: 'flex-start',
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* Mobile overlay backdrop */}
      {isMobile && sidebarOpen && (
        <Box
          onClick={() => setSidebarOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.4)',
            zIndex: 1100,
          }}
        />
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'white',
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: { xs: 2, md: 4 },
            pb: { xs: 3, md: 4 },
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              maxWidth: { xs: '100%', lg: '85%', xl: '80%' },
              width: '100%',
              opacity: transicao ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            {showCertificateView ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: { xs: 6, md: 10 },
                  px: { xs: 3, md: 6 },
                  bgcolor: '#ffffff',
                  borderRadius: '24px',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.06)',
                  border: '1px solid',
                  borderColor: 'rgba(0,0,0,0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background:
                    'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                }}
              >
                <WorkspacePremium
                  sx={{ fontSize: 100, color: '#FFB74D', mb: 3 }}
                />
                <Typography
                  variant='h3'
                  fontWeight='900'
                  sx={{ color: '#0A2540', mb: 2 }}
                >
                  Parabéns por completar o curso!
                </Typography>
                <Typography
                  variant='h6'
                  color='text.secondary'
                  sx={{ mb: 6, maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
                >
                  Você concluiu todas as etapas e avaliações. Seu certificado já
                  está disponível para download e compartilhamento!
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    variant='contained'
                    color='success'
                    startIcon={<WorkspacePremium />}
                    onClick={handleDownloadCertificate}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      borderRadius: '12px',
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      boxShadow: '0 8px 24px rgba(34, 197, 94, 0.25)',
                    }}
                  >
                    Download Certificado
                  </Button>
                  <Button
                    variant='outlined'
                    color='success'
                    startIcon={<WorkspacePremium />}
                    onClick={handleViewCertificate}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      borderRadius: '12px',
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                      },
                    }}
                  >
                    Visualizar Certificado
                  </Button>
                  <Button
                    variant='contained'
                    startIcon={<LinkedIn />}
                    onClick={handleShareLinkedIn}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      borderRadius: '12px',
                      bgcolor: '#0077b5',
                      boxShadow: '0 8px 24px rgba(0, 119, 181, 0.25)',
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      '&:hover': {
                        bgcolor: '#005582',
                        boxShadow: '0 12px 32px rgba(0, 119, 181, 0.3)',
                      },
                    }}
                  >
                    Compartilhar no LinkedIn
                  </Button>
                </Box>
              </Box>
            ) : aulaDetalheBlocked || aulaDetalheError ? (
              <Alert
                severity={aulaDetalheBlocked ? 'warning' : 'error'}
                sx={{ mt: 2 }}
              >
                {aulaDetalheError}
              </Alert>
            ) : aulaDetalheLoading && !aulaDetalhe ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 280,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              aulaAtual && (
                <>
                  <>
                    <Typography
                      variant='caption'
                      fontWeight='bold'
                      color='primary'
                      gutterBottom
                    >
                      {moduloAtual.modulo_titulo}
                    </Typography>
                    <Typography variant='h4' fontWeight='bold' sx={{ mb: 1 }}>
                      {aulaAtual.video_info?.title || aulaAtual.titulo}
                    </Typography>

                    {/* Description at top for feedback, avaliacao and questionario */}
                    {(currentTipo === 'feedback' ||
                      currentTipo === 'avaliacao' ||
                      currentTipo === 'questionario') &&
                      aulaAtual.descricao && (
                        <Box
                          sx={{
                            mb: 3,
                            p: 2,
                            bgcolor: '#f0f4ff',
                            borderRadius: 2,
                            border: '1px solid #d0dcf0',
                          }}
                        >
                          <Typography variant='body1' color='text.secondary'>
                            {aulaAtual.descricao}
                          </Typography>
                        </Box>
                      )}

                    {/* Conteúdo Específico por Tipo */}
                    {(currentTipo === 'video' ||
                      (aulaAtual.video_info && currentTipo === 'video')) && (
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          maxWidth: '1200px',
                          mx: 'auto',
                          aspectRatio: '16/9',
                          bgcolor: '#000',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow:
                            '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                          mb: 4,
                        }}
                      >
                        <iframe
                          id='vimeo-player'
                          src={(() => {
                            let src = '';
                            // Prefer link (may include #t= resume) over embed_url
                            if (aulaAtual.link) {
                              src = aulaAtual.link.includes('player.vimeo.com')
                                ? aulaAtual.link
                                : aulaAtual.link.replace(
                                    'vimeo.com',
                                    'player.vimeo.com/video'
                                  );
                            } else if (aulaAtual.video_info?.embed_url) {
                              const match =
                                aulaAtual.video_info.embed_url.match(
                                  /src=["']([^"']+)["']/
                                );
                              src = match
                                ? match[1]
                                : aulaAtual.video_info.embed_url;
                            }

                            if (src) {
                              // Prefer explicit tempo_segundos when link has no #t=
                              if (
                                !/#t=/i.test(src) &&
                                Number(aulaAtual.tempo_segundos) > 0
                              ) {
                                src += `#t=${Math.floor(aulaAtual.tempo_segundos)}s`;
                              }
                              // Insert API query params before the hash so #t= is preserved
                              const hashIndex = src.indexOf('#');
                              const hash =
                                hashIndex >= 0 ? src.slice(hashIndex) : '';
                              const withoutHash =
                                hashIndex >= 0 ? src.slice(0, hashIndex) : src;
                              const separator = withoutHash.includes('?')
                                ? '&'
                                : '?';
                              src = `${withoutHash}${separator}api=1&player_id=vimeo-player${hash}`;
                            }
                            return src;
                          })()}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                          }}
                          frameBorder='0'
                          allow='autoplay; fullscreen; picture-in-picture'
                          allowFullScreen
                          title={aulaAtual.titulo}
                        />
                      </Box>
                    )}

                    {currentTipo === 'atividade' && (
                      <Box
                        sx={{
                          mb: 4,
                          p: 3,
                          bgcolor: '#f8fafc',
                          borderRadius: '12px',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <Assignment color='primary' sx={{ mr: 1 }} />
                          <Typography variant='h6' fontWeight='bold'>
                            Atividade Prática
                          </Typography>
                        </Box>
                        <Typography variant='body1' sx={{ mb: 3 }}>
                          {aulaAtual.descricao ||
                            'Esta atividade requer o uso do Gêmeo Digital para prática.'}
                        </Typography>
                        {aulaAtual.link && (
                          <Button
                            variant='contained'
                            color='primary'
                            startIcon={<Launch />}
                            href={aulaAtual.link}
                            target='_blank'
                            sx={{
                              px: 4,
                              py: 1.5,
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 'bold',
                            }}
                          >
                            Abrir Gêmeo Digital
                          </Button>
                        )}
                      </Box>
                    )}

                    {(currentTipo === 'atividade_teorica' ||
                      currentTipo === 'avaliacao') && (
                      <Box sx={{ mb: 4 }}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
                        >
                          <Assignment
                            color='primary'
                            sx={{ mr: 1, fontSize: 28 }}
                          />
                          <Typography variant='h5' fontWeight='bold'>
                            {currentTipo === 'avaliacao'
                              ? 'Avaliação'
                              : 'Atividade Teórica'}
                          </Typography>
                        </Box>

                        {/* Pre-evaluation gate: only for 'avaliacao' type */}
                        {currentTipo === 'avaliacao' &&
                          !avaliacaoIniciada &&
                          !avaliacaoEnviada &&
                          !aulaAtual.concluido &&
                          !avaliacaoLoading && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 4,
                              }}
                            >
                              <Paper
                                elevation={3}
                                sx={{
                                  p: 4,
                                  maxWidth: 560,
                                  width: '100%',
                                  borderRadius: '20px',
                                  textAlign: 'center',
                                  border: '1px solid',
                                  borderColor: 'warning.200',
                                  bgcolor: 'background.paper',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: '50%',
                                    bgcolor: 'warning.50',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2,
                                  }}
                                >
                                  <Assignment
                                    sx={{ fontSize: 36, color: 'warning.main' }}
                                  />
                                </Box>
                                <Typography
                                  variant='h5'
                                  fontWeight='bold'
                                  gutterBottom
                                >
                                  Avaliação Teórica
                                </Typography>
                                <Typography
                                  variant='body1'
                                  color='text.secondary'
                                  sx={{ mb: 3 }}
                                >
                                  {aulaAtual?.descricao ||
                                    'Esta avaliação irá medir seu conhecimento sobre os temas abordados no módulo.'}
                                </Typography>
                                <Box
                                  sx={{
                                    bgcolor: 'warning.50',
                                    border: '1px solid',
                                    borderColor: 'warning.200',
                                    borderRadius: '12px',
                                    p: 2,
                                    mb: 3,
                                    textAlign: 'left',
                                  }}
                                >
                                  <Typography
                                    variant='subtitle2'
                                    color='warning.dark'
                                    gutterBottom
                                    fontWeight='bold'
                                  >
                                    ⚠️ Informações Importantes:
                                  </Typography>
                                  <Typography variant='body2' sx={{ mb: 0.5 }}>
                                    • <strong>Tempo limite:</strong> 1 hora (60
                                    minutos)
                                  </Typography>
                                  {(() => {
                                    const maxT =
                                      aulaAtual?.tentativas_max ??
                                      avaliacaoMeta?.nr_tentativas_max ??
                                      3;
                                    const finT = avaliacaoTentativas.filter(
                                      t => t.dh_fim
                                    ).length;
                                    const restT = Math.max(0, maxT - finT);
                                    return (
                                      <>
                                        <Typography
                                          variant='body2'
                                          sx={{ mb: 0.5 }}
                                        >
                                          •{' '}
                                          <strong>Máximo de tentativas:</strong>{' '}
                                          {maxT < 9999
                                            ? `${maxT} tentativas`
                                            : 'ilimitadas'}
                                        </Typography>
                                        {finT > 0 && maxT < 9999 && (
                                          <Typography
                                            variant='body2'
                                            sx={{
                                              mb: 0.5,
                                              color: 'warning.dark',
                                              fontWeight: 'bold',
                                            }}
                                          >
                                            •{' '}
                                            <strong>
                                              Tentativas restantes:
                                            </strong>{' '}
                                            {restT} de {maxT}
                                          </Typography>
                                        )}
                                      </>
                                    );
                                  })()}
                                  <Typography
                                    variant='body2'
                                    color='error.main'
                                    sx={{ mt: 1 }}
                                  >
                                    O cronômetro iniciará assim que você clicar
                                    em "Iniciar Avaliação" e não poderá ser
                                    pausado.
                                  </Typography>
                                </Box>
                                <Button
                                  variant='contained'
                                  size='large'
                                  color='primary'
                                  fullWidth
                                  onClick={() => {
                                    setAvaliacaoIniciada(true);
                                    setAvaliacaoSecondsLeft(3600);
                                    if (contentRef.current) {
                                      contentRef.current.scrollTo({
                                        top: 0,
                                        behavior: 'smooth',
                                      });
                                    }
                                    window.scrollTo({
                                      top: 0,
                                      behavior: 'smooth',
                                    });
                                  }}
                                  sx={{
                                    py: 1.5,
                                    borderRadius: '12px',
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                  }}
                                >
                                  Iniciar Avaliação
                                </Button>
                              </Paper>
                            </Box>
                          )}

                        {/* Timer bar — shown while evaluation is in progress */}
                        {currentTipo === 'avaliacao' &&
                          avaliacaoIniciada &&
                          !avaliacaoEnviada && (
                            <Box sx={{ mb: 3 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  Tempo restante
                                </Typography>
                                <Typography
                                  variant='body2'
                                  fontWeight='bold'
                                  color={
                                    avaliacaoSecondsLeft < 300
                                      ? 'error.main'
                                      : 'text.primary'
                                  }
                                >
                                  {`${String(Math.floor(avaliacaoSecondsLeft / 3600)).padStart(2, '0')}:${String(Math.floor((avaliacaoSecondsLeft % 3600) / 60)).padStart(2, '0')}:${String(avaliacaoSecondsLeft % 60).padStart(2, '0')}`}
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant='determinate'
                                value={(avaliacaoSecondsLeft / 3600) * 100}
                                color={
                                  avaliacaoSecondsLeft < 300
                                    ? 'error'
                                    : 'primary'
                                }
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                          )}

                        {avaliacaoLoading && avaliacaoQuestoes.length === 0 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              p: 4,
                            }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : (
                          (currentTipo !== 'avaliacao' ||
                            avaliacaoIniciada ||
                            avaliacaoEnviada ||
                            aulaAtual.concluido) && (
                            <>
                              {avaliacaoEnviada &&
                                (() => {
                                  const notaCorteCard =
                                    avaliacaoMeta?.nota_corte ?? 7;
                                  const aprovadoCard =
                                    avaliacaoEnviada.bo_aprovado ??
                                    avaliacaoEnviada.nota >= notaCorteCard;
                                  const maxTentativasCard =
                                    aulaAtual?.tentativas_max ??
                                    avaliacaoMeta?.nr_tentativas_max ??
                                    3;
                                  const tentativasFinalizadasCard =
                                    avaliacaoTentativas.filter(
                                      t => t.dh_fim
                                    ).length;
                                  const restantesCard =
                                    avaliacaoEnviada.tentativas_restantes !==
                                      undefined &&
                                    avaliacaoEnviada.tentativas_restantes !==
                                      null
                                      ? avaliacaoEnviada.tentativas_restantes
                                      : Math.max(
                                          0,
                                          maxTentativasCard -
                                            tentativasFinalizadasCard
                                        );
                                  const semTentativasCard = restantesCard <= 0;

                                  return (
                                    <Paper
                                      sx={{
                                        p: 3,
                                        mb: 4,
                                        borderRadius: '16px',
                                        bgcolor: aprovadoCard
                                          ? 'success.50'
                                          : 'error.50',
                                        border: '1px solid',
                                        borderColor: aprovadoCard
                                          ? 'success.200'
                                          : 'error.200',
                                      }}
                                    >
                                      {/* Header */}
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1.5,
                                          mb: 2,
                                        }}
                                      >
                                        {aprovadoCard ? (
                                          <CheckCircleOutline
                                            sx={{
                                              fontSize: 36,
                                              color: 'success.main',
                                            }}
                                          />
                                        ) : (
                                          <ErrorIcon
                                            sx={{
                                              fontSize: 36,
                                              color: 'error.main',
                                            }}
                                          />
                                        )}
                                        <Box>
                                          <Typography
                                            variant='h6'
                                            fontWeight='bold'
                                            color={
                                              aprovadoCard
                                                ? 'success.main'
                                                : 'error.main'
                                            }
                                          >
                                            {aprovadoCard
                                              ? 'Parabéns! Você foi aprovado.'
                                              : 'Você não atingiu a nota mínima.'}
                                          </Typography>
                                          <Typography
                                            variant='body2'
                                            color='text.secondary'
                                          >
                                            {aprovadoCard
                                              ? 'Seu desempenho foi suficiente para avançar no curso.'
                                              : `Nota mínima para aprovação: ${notaCorteCard.toFixed ? notaCorteCard.toFixed(1) : notaCorteCard}`}
                                          </Typography>
                                        </Box>
                                      </Box>

                                      {/* Score details */}
                                      <Box
                                        sx={{
                                          display: 'grid',
                                          gridTemplateColumns:
                                            'repeat(auto-fit, minmax(120px, 1fr))',
                                          gap: 1.5,
                                          mb: 2,
                                        }}
                                      >
                                        {avaliacaoEnviada.nota !== undefined &&
                                          avaliacaoEnviada.nota !== null && (
                                            <Box
                                              sx={{
                                                textAlign: 'center',
                                                p: 1.5,
                                                bgcolor: 'white',
                                                borderRadius: '10px',
                                                border: '1px solid',
                                                borderColor: aprovadoCard
                                                  ? 'success.100'
                                                  : 'error.100',
                                              }}
                                            >
                                              <Typography
                                                variant='h5'
                                                fontWeight='bold'
                                                color={
                                                  aprovadoCard
                                                    ? 'success.main'
                                                    : 'error.main'
                                                }
                                              >
                                                {Number(
                                                  avaliacaoEnviada.nota
                                                ).toFixed(1)}
                                              </Typography>
                                              <Typography
                                                variant='caption'
                                                color='text.secondary'
                                              >
                                                Sua nota
                                              </Typography>
                                            </Box>
                                          )}
                                        {(avaliacaoEnviada.qt_corretas !==
                                          undefined ||
                                          avaliacaoEnviada.acertos !==
                                            undefined) && (
                                          <Box
                                            sx={{
                                              textAlign: 'center',
                                              p: 1.5,
                                              bgcolor: 'white',
                                              borderRadius: '10px',
                                              border: '1px solid',
                                              borderColor: 'divider',
                                            }}
                                          >
                                            <Typography
                                              variant='h5'
                                              fontWeight='bold'
                                            >
                                              {avaliacaoEnviada.qt_corretas ??
                                                avaliacaoEnviada.acertos}
                                              <Typography
                                                component='span'
                                                variant='body2'
                                                color='text.secondary'
                                              >
                                                /
                                                {avaliacaoEnviada.qt_total ??
                                                  avaliacaoEnviada.total ??
                                                  (avaliacaoQuestoes?.length ||
                                                    aulaAtual?.atividades
                                                      ?.length ||
                                                    '')}
                                              </Typography>
                                            </Typography>
                                            <Typography
                                              variant='caption'
                                              color='text.secondary'
                                            >
                                              Acertos
                                            </Typography>
                                          </Box>
                                        )}
                                        {!aprovadoCard && (
                                          <Box
                                            sx={{
                                              textAlign: 'center',
                                              p: 1.5,
                                              bgcolor: 'white',
                                              borderRadius: '10px',
                                              border: '1px solid',
                                              borderColor: 'divider',
                                            }}
                                          >
                                            <Typography
                                              variant='h5'
                                              fontWeight='bold'
                                              color={
                                                semTentativasCard
                                                  ? 'error.main'
                                                  : 'text.primary'
                                              }
                                            >
                                              {restantesCard}
                                              <Typography
                                                component='span'
                                                variant='body2'
                                                color='text.secondary'
                                              >
                                                /
                                                {maxTentativasCard < 9999
                                                  ? maxTentativasCard
                                                  : '∞'}
                                              </Typography>
                                            </Typography>
                                            <Typography
                                              variant='caption'
                                              color='text.secondary'
                                            >
                                              Tent. restantes
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>

                                      {/* Completion Metadata */}
                                      <Box
                                        sx={{
                                          mt: 2,
                                          pt: 2,
                                          borderTop: '1px solid',
                                          borderColor: 'divider',
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          gap: 2,
                                          justifyContent: 'center',
                                        }}
                                      >
                                        {avaliacaoEnviada.dh_fim && (
                                          <Typography
                                            variant='caption'
                                            color='text.secondary'
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 0.5,
                                            }}
                                          >
                                            <CalendarToday
                                              sx={{ fontSize: 14 }}
                                            />
                                            Concluído em:{' '}
                                            {new Date(
                                              avaliacaoEnviada.dh_fim
                                            ).toLocaleString('pt-BR')}
                                          </Typography>
                                        )}
                                        {avaliacaoEnviada.dh_inicio &&
                                          avaliacaoEnviada.dh_fim &&
                                          (() => {
                                            const start = new Date(
                                              avaliacaoEnviada.dh_inicio
                                            );
                                            const end = new Date(
                                              avaliacaoEnviada.dh_fim
                                            );
                                            const diff = Math.floor(
                                              (end - start) / 1000
                                            ); // seconds
                                            const mins = Math.floor(diff / 60);
                                            const secs = diff % 60;
                                            return (
                                              <Typography
                                                variant='caption'
                                                color='text.secondary'
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 0.5,
                                                }}
                                              >
                                                <AccessTime
                                                  sx={{ fontSize: 14 }}
                                                />
                                                Tempo:{' '}
                                                {mins > 0 ? `${mins}min ` : ''}
                                                {secs}s
                                              </Typography>
                                            );
                                          })()}
                                      </Box>

                                      {/* Continuar Button when Approved */}
                                      {aprovadoCard && (
                                        <Button
                                          variant='contained'
                                          fullWidth
                                          onClick={() => handleProximaAula()}
                                          endIcon={<PlayArrow />}
                                          sx={{
                                            borderRadius: '10px',
                                            mt: 3,
                                            fontWeight: 'bold',
                                            py: 1.5,
                                            background:
                                              'linear-gradient(135deg, #0A2540 0%, #173855 100%)',
                                            color: '#FFF',
                                            '&:hover': {
                                              background:
                                                'linear-gradient(135deg, #0F282D 0%, #1A4060 100%)',
                                            },
                                          }}
                                        >
                                          Próximo Conteúdo
                                        </Button>
                                      )}

                                      {/* Warning for failed state */}
                                      {!aprovadoCard && (
                                        <Alert
                                          severity={
                                            semTentativasCard
                                              ? 'error'
                                              : 'warning'
                                          }
                                          sx={{ borderRadius: '10px', mt: 2 }}
                                        >
                                          {semTentativasCard
                                            ? 'Você esgotou todas as tentativas. O progresso deste módulo será resetado e você precisará refazer todas as aulas.'
                                            : `Atenção: se você esgotar todas as ${maxTentativasCard < 9999 ? maxTentativasCard : ''} tentativas sem aprovação, o progresso do módulo inteiro será resetado e você deverá refazer as aulas.`}
                                        </Alert>
                                      )}

                                      {/* Reset module button when failed and NO remaining attempts */}
                                      {!aprovadoCard && semTentativasCard && (
                                        <Button
                                          variant='contained'
                                          color='error'
                                          fullWidth
                                          onClick={handleReiniciarModulo}
                                          sx={{
                                            borderRadius: '10px',
                                            mt: 2,
                                            fontWeight: 'bold',
                                          }}
                                        >
                                          Reiniciar Módulo
                                        </Button>
                                      )}

                                      {/* Retry button when failed and has remaining attempts */}
                                      {!aprovadoCard && !semTentativasCard && (
                                        <Button
                                          variant='contained'
                                          color='primary'
                                          fullWidth
                                          onClick={async () => {
                                            // Reset evaluation state so pre-evaluation gate ("Iniciar Avaliação") is shown again.
                                            setAvaliacaoEnviada(null);
                                            setRespostas({});
                                            setQuestaoFeedbacks({});
                                            setAvaliacaoIniciada(false);
                                            setAvaliacaoSecondsLeft(3600);

                                            // Scroll to top of page smoothly so user is focused on the start card
                                            if (contentRef.current) {
                                              contentRef.current.scrollTo({
                                                top: 0,
                                                behavior: 'smooth',
                                              });
                                            }
                                            window.scrollTo({
                                              top: 0,
                                              behavior: 'smooth',
                                            });

                                            // Start a new attempt on the server and refresh attempt count
                                            try {
                                              const newAttempt =
                                                await startContent(
                                                  aulaAtual.id_curso_conteudo_usuario
                                                );
                                              if (
                                                newAttempt?.id_curso_tentativa_usuario
                                              ) {
                                                setCurrentTentativaId(
                                                  newAttempt.id_curso_tentativa_usuario
                                                );
                                              }
                                              await fetchAvaliacaoData();
                                            } catch {
                                              // ignore
                                            }
                                          }}
                                          sx={{
                                            borderRadius: '10px',
                                            mt: 2,
                                            fontWeight: 'bold',
                                          }}
                                        >
                                          Tentar Novamente ({restantesCard}{' '}
                                          restante
                                          {restantesCard !== 1 ? 's' : ''})
                                        </Button>
                                      )}
                                    </Paper>
                                  );
                                })()}

                              {avaliacaoQuestoes.map((questao, index) => {
                                const notaCorte =
                                  avaliacaoMeta?.nota_corte ?? 7;
                                const aprovadoValue =
                                  avaliacaoEnviada?.bo_aprovado ||
                                  avaliacaoEnviada?.nota >= notaCorte;
                                const qId =
                                  questao.id_curso_avaliacao ||
                                  questao.id_curso_avaliacao_questao;
                                const questionLevelCorrect =
                                  !!questaoFeedbacks[qId];
                                const questionHasFeedback =
                                  (!!avaliacaoEnviada || aulaAtual.concluido) &&
                                  questaoFeedbacks.hasOwnProperty(qId);
                                const showResult =
                                  (!!avaliacaoEnviada && aprovadoValue) ||
                                  aulaAtual.concluido;

                                return (
                                  <Box
                                    key={`q-box-${index}`}
                                    sx={{
                                      mb: 4,
                                      p: 3,
                                      bgcolor: questionHasFeedback
                                        ? questionLevelCorrect
                                          ? '#f0fdf4'
                                          : '#fef2f2'
                                        : 'white',
                                      border: '1px solid',
                                      borderColor: questionHasFeedback
                                        ? questionLevelCorrect
                                          ? 'success.light'
                                          : 'error.light'
                                        : 'divider',
                                      borderRadius: '12px',
                                    }}
                                  >
                                    <Typography
                                      variant='subtitle1'
                                      fontWeight='bold'
                                      gutterBottom
                                    >
                                      {index + 1}. {questao.nm_questao}
                                    </Typography>

                                    <FormControl component='fieldset' fullWidth>
                                      {questao.cd_tipo_questao === 'O' ? (
                                        <RadioGroup
                                          name={`questao-${index}`}
                                          value={
                                            respostas[index]
                                              ? String(respostas[index])
                                              : ''
                                          }
                                          onChange={e =>
                                            handleRespostaChange(
                                              index,
                                              e.target.value
                                            )
                                          }
                                        >
                                          {(
                                            questao.questoes ||
                                            questao.alternativas
                                          )
                                            ?.sort(
                                              (a, b) => a.nr_ordem - b.nr_ordem
                                            )
                                            .map(alt => {
                                              const altId =
                                                alt.id_curso_avaliacao_usuario ||
                                                alt.id_curso_avaliacao_questao_alt ||
                                                alt.id_curso_avaliacao_questao;
                                              const isSelected =
                                                respostas[index] &&
                                                String(respostas[index]) ===
                                                  String(altId);
                                              const alternativeIsCorrect =
                                                !!alt.bo_ok;

                                              // Only paint success/error AFTER the evaluation has been
                                              // submitted and we have actual feedback for this question.
                                              const paintAsSuccess =
                                                showResult &&
                                                questionHasFeedback &&
                                                isSelected &&
                                                questionLevelCorrect;
                                              const paintAsError =
                                                showResult &&
                                                questionHasFeedback &&
                                                isSelected &&
                                                !questionLevelCorrect;

                                              return (
                                                <Paper
                                                  key={altId}
                                                  elevation={0}
                                                  sx={{
                                                    mb: 1.5,
                                                    border: '1px solid',
                                                    borderColor: showResult
                                                      ? paintAsSuccess
                                                        ? 'success.main'
                                                        : paintAsError
                                                          ? 'error.main'
                                                          : 'divider'
                                                      : isSelected
                                                        ? 'primary.main'
                                                        : 'divider',
                                                    borderRadius: 2,
                                                    bgcolor: showResult
                                                      ? paintAsSuccess
                                                        ? 'success.50'
                                                        : paintAsError
                                                          ? 'error.50'
                                                          : 'transparent'
                                                      : isSelected
                                                        ? 'primary.50'
                                                        : 'transparent',
                                                  }}
                                                >
                                                  <FormControlLabel
                                                    value={String(altId)}
                                                    control={
                                                      <Radio
                                                        color='primary'
                                                        disabled={
                                                          !!avaliacaoEnviada
                                                        }
                                                      />
                                                    }
                                                    label={
                                                      <Box
                                                        sx={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: 1,
                                                        }}
                                                      >
                                                        {alt.nm_alternativa}
                                                        {paintAsSuccess && (
                                                          <CheckCircle
                                                            color='success'
                                                            sx={{
                                                              fontSize: 18,
                                                            }}
                                                          />
                                                        )}
                                                        {paintAsError && (
                                                          <ErrorIcon
                                                            color='error'
                                                            sx={{
                                                              fontSize: 18,
                                                            }}
                                                          />
                                                        )}
                                                      </Box>
                                                    }
                                                    sx={{
                                                      width: '100%',
                                                      m: 0,
                                                      p: 1.5,
                                                    }}
                                                  />
                                                </Paper>
                                              );
                                            })}
                                        </RadioGroup>
                                      ) : (
                                        <Paper
                                          variant='outlined'
                                          sx={{
                                            p: 0,
                                            borderRadius: '8px',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            overflow: 'hidden',
                                            boxShadow:
                                              '0 2px 4px rgba(0,0,0,0.02)',
                                          }}
                                        >
                                          <textarea
                                            className='w-full p-3 text-sm focus:outline-none'
                                            rows={4}
                                            placeholder='Escreva sua resposta aqui...'
                                            disabled={!!avaliacaoEnviada}
                                            value={respostas[index] || ''}
                                            onChange={e => {
                                              handleRespostaChange(
                                                index,
                                                e.target.value
                                              );
                                            }}
                                            style={{
                                              width: '100%',
                                              border: 'none',
                                              resize: 'none',
                                              padding: '16px',
                                              fontFamily: 'inherit',
                                              fontSize: '1rem',
                                              backgroundColor: 'white',
                                            }}
                                          />
                                        </Paper>
                                      )}
                                    </FormControl>
                                  </Box>
                                );
                              })}

                              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                {(() => {
                                  const notaCorte =
                                    avaliacaoMeta?.nota_corte ?? 7;
                                  const aprovado =
                                    avaliacaoEnviada?.bo_aprovado ||
                                    avaliacaoEnviada?.nota >= notaCorte;
                                  const maxTentativas =
                                    avaliacaoMeta?.nr_tentativas_max ?? 999;
                                  const tentativasFinalizadas =
                                    avaliacaoTentativas.filter(t => t.dh_fim);
                                  const semTentativas =
                                    tentativasFinalizadas.length >=
                                    maxTentativas;
                                  const allAnswered = avaliacaoQuestoes.every(
                                    (q, i) => {
                                      if (q.cd_tipo_questao !== 'O')
                                        return true;
                                      return (
                                        respostas[i] != null &&
                                        String(respostas[i]).trim() !== ''
                                      );
                                    }
                                  );

                                  if (aprovado) {
                                    // Passed — no retry needed
                                    return null;
                                  }

                                  if (semTentativas && avaliacaoEnviada) {
                                    return (
                                      <Box sx={{ width: '100%' }}>
                                        <Alert
                                          severity='warning'
                                          sx={{ mb: 2 }}
                                        >
                                          Você esgotou todas as suas tentativas
                                          para esta avaliação.
                                        </Alert>
                                        <Button
                                          variant='contained'
                                          color='error'
                                          size='large'
                                          fullWidth
                                          onClick={handleReiniciarModulo}
                                          sx={{
                                            py: 2,
                                            borderRadius: '12px',
                                            fontWeight: 'bold',
                                          }}
                                        >
                                          Reiniciar Módulo
                                        </Button>
                                      </Box>
                                    );
                                  }

                                  return (
                                    <>
                                      {!avaliacaoEnviada && (
                                        <Button
                                          variant='contained'
                                          size='large'
                                          fullWidth
                                          onClick={() =>
                                            setConfirmSubmitAvaliacaoOpen(true)
                                          }
                                          disabled={
                                            loading ||
                                            avaliacaoQuestoes.length === 0 ||
                                            !allAnswered
                                          }
                                          sx={{
                                            py: 2,
                                            borderRadius: '12px',
                                            fontWeight: 'bold',
                                          }}
                                        >
                                          {loading ? (
                                            <CircularProgress size={24} />
                                          ) : (
                                            'Enviar Respostas'
                                          )}
                                        </Button>
                                      )}
                                      {avaliacaoEnviada && !semTentativas && (
                                        <Button
                                          variant='outlined'
                                          size='large'
                                          fullWidth
                                          onClick={async () => {
                                            // Reset all evaluation state so the pre-evaluation
                                            // gate ("Iniciar Avaliação") is shown again.
                                            setAvaliacaoEnviada(null);
                                            setRespostas({});
                                            setQuestaoFeedbacks({});
                                            setAvaliacaoIniciada(false);
                                            setAvaliacaoSecondsLeft(3600);
                                            // Start a new attempt on the server and refresh
                                            // attempts without triggering a full page spinner.
                                            try {
                                              const newAttempt =
                                                await startContent(
                                                  aulaAtual.id_curso_conteudo_usuario
                                                );
                                              if (
                                                newAttempt?.id_curso_tentativa_usuario
                                              ) {
                                                setCurrentTentativaId(
                                                  newAttempt.id_curso_tentativa_usuario
                                                );
                                              }
                                              await fetchAvaliacaoData();
                                            } catch {
                                              // ignore
                                            }
                                          }}
                                          sx={{
                                            py: 2,
                                            borderRadius: '12px',
                                            fontWeight: 'bold',
                                          }}
                                        >
                                          {(() => {
                                            const remaining =
                                              avaliacaoEnviada?.tentativas_restantes !==
                                              undefined
                                                ? avaliacaoEnviada.tentativas_restantes
                                                : Math.max(
                                                    0,
                                                    maxTentativas -
                                                      tentativasFinalizadas.length
                                                  );
                                            return `Tentar Novamente (${remaining} restante${remaining !== 1 ? 's' : ''})`;
                                          })()}
                                        </Button>
                                      )}
                                    </>
                                  );
                                })()}
                              </Box>
                            </>
                          )
                        )}
                      </Box>
                    )}

                    {currentTipo === 'feedback' && (
                      <Box
                        sx={{
                          mb: 4,
                          p: 4,
                          bgcolor: '#f8fafc',
                          borderRadius: '12px',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {feedbackSent || aulaAtual.concluido ? (
                          <Box
                            sx={{
                              textAlign: 'center',
                              py: 4,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              bgcolor: '#f0fdf4',
                              borderRadius: '8px',
                              border: '1px solid',
                              borderColor: 'success.light',
                            }}
                          >
                            <CheckCircleOutline
                              sx={{
                                fontSize: 64,
                                color: 'success.main',
                                mb: 2,
                              }}
                            />
                            <Typography
                              variant='h5'
                              fontWeight='bold'
                              gutterBottom
                            >
                              Feedback Enviado!
                            </Typography>
                            <Typography variant='body1' color='text.secondary'>
                              Obrigado por avaliar este módulo. Sua opinião é
                              muito importante para nós.
                            </Typography>
                            <Button
                              variant='contained'
                              onClick={() => handleProximaAula()}
                              endIcon={<PlayArrow />}
                              sx={{
                                mt: 3,
                                borderRadius: '10px',
                                fontWeight: 'bold',
                                py: 1.5,
                                px: 4,
                                background:
                                  'linear-gradient(135deg, #0A2540 0%, #173855 100%)',
                                color: '#FFF',
                                '&:hover': {
                                  background:
                                    'linear-gradient(135deg, #0F282D 0%, #1A4060 100%)',
                                },
                              }}
                            >
                              Próximo Conteúdo
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            <Box sx={{ mb: 4, textAlign: 'center' }}>
                              <Typography
                                variant='h5'
                                fontWeight='bold'
                                gutterBottom
                              >
                                Avalie este Módulo
                              </Typography>
                              <Typography
                                variant='body1'
                                color='text.secondary'
                              >
                                Por favor, preencha o questionário de feedback
                                abaixo.
                              </Typography>
                            </Box>

                            {feedbackQuestoes.map((questao, index) => (
                              <Box key={questao.id_curso_feed} sx={{ mb: 4 }}>
                                <Typography
                                  variant='subtitle1'
                                  fontWeight='bold'
                                  gutterBottom
                                >
                                  {index + 1}. {questao.nm_questao}
                                </Typography>

                                {questao.cd_tipo_questao === 'O' &&
                                questao.alternativas &&
                                questao.alternativas.length > 0 ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      mb: 2,
                                    }}
                                  >
                                    {[1, 2, 3, 4, 5].map(star => {
                                      // Encontrar alternativa com nr_ordem igual a star (assumindo que 1-5 estrelas = 1-5 nr_ordem)
                                      const alt = questao.alternativas?.find(
                                        a => a.nr_ordem === star
                                      );
                                      if (!alt) return null;

                                      return (
                                        <Box
                                          key={star}
                                          onClick={() =>
                                            setFeedbackRespostas(prev => ({
                                              ...prev,
                                              [questao.id_curso_feed]: String(
                                                alt.id_curso_feed_questao
                                              ),
                                            }))
                                          }
                                          sx={{
                                            cursor: 'pointer',
                                            mx: 1,
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                              transform: 'scale(1.2)',
                                            },
                                          }}
                                          title={alt.nm_alternativa} // Mostra o texto da alternativa ao passar o mouse
                                        >
                                          {/* Se a alternativa escolhida tem nr_ordem >= a esta estrela, colore.
                                              Precisamos achar a nr_ordem da alternativa atualmente selecionada */}
                                          {(() => {
                                            const selectedAltId =
                                              feedbackRespostas[
                                                questao.id_curso_feed
                                              ];
                                            const selectedAlt =
                                              questao.alternativas?.find(
                                                a =>
                                                  String(
                                                    a.id_curso_feed_questao
                                                  ) === selectedAltId
                                              );
                                            const selectedRating =
                                              selectedAlt?.nr_ordem || 0;

                                            return star <= selectedRating ? (
                                              <Star
                                                sx={{
                                                  fontSize: 48,
                                                  color: '#fbbf24',
                                                  filter:
                                                    'drop-shadow(0 4px 6px rgba(251, 191, 36, 0.4))',
                                                }}
                                              />
                                            ) : (
                                              <StarBorder
                                                sx={{
                                                  fontSize: 48,
                                                  color: '#cbd5e1',
                                                }}
                                              />
                                            );
                                          })()}
                                        </Box>
                                      );
                                    })}
                                    {/* Mostra o texto da alternativa selecionada ao lado */}
                                    <Box sx={{ ml: 2, minWidth: 150 }}>
                                      {(() => {
                                        const selectedAltId =
                                          feedbackRespostas[
                                            questao.id_curso_feed
                                          ];
                                        const selectedAlt =
                                          questao.alternativas?.find(
                                            a =>
                                              String(
                                                a.id_curso_feed_questao
                                              ) === selectedAltId
                                          );
                                        return (
                                          <Typography
                                            variant='body2'
                                            color='text.secondary'
                                            fontWeight='bold'
                                          >
                                            {selectedAlt?.nm_alternativa || ''}
                                          </Typography>
                                        );
                                      })()}
                                    </Box>
                                  </Box>
                                ) : (
                                  <Paper
                                    variant='outlined'
                                    sx={{
                                      p: 0,
                                      borderRadius: '8px',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      overflow: 'hidden',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                    }}
                                  >
                                    <textarea
                                      className='w-full p-3 text-sm focus:outline-none'
                                      rows={4}
                                      placeholder='Escreva sua resposta aqui...'
                                      value={
                                        feedbackRespostas[
                                          questao.id_curso_feed
                                        ] || ''
                                      }
                                      onChange={e =>
                                        setFeedbackRespostas(prev => ({
                                          ...prev,
                                          [questao.id_curso_feed]:
                                            e.target.value,
                                        }))
                                      }
                                      style={{
                                        width: '100%',
                                        border: 'none',
                                        resize: 'none',
                                        padding: '16px',
                                        fontFamily: 'inherit',
                                        fontSize: '1rem',
                                        backgroundColor: 'white',
                                      }}
                                    />
                                  </Paper>
                                )}
                              </Box>
                            ))}

                            <Button
                              variant='contained'
                              size='large'
                              onClick={handleEnviarFeedback}
                              disabled={
                                loading ||
                                feedbackQuestoes.length === 0 ||
                                feedbackQuestoes.some(q => {
                                  const isObjective =
                                    q.cd_tipo_questao === 'O' &&
                                    q.alternativas &&
                                    q.alternativas.length > 0;
                                  return (
                                    isObjective &&
                                    !feedbackRespostas[q.id_curso_feed]
                                  );
                                })
                              }
                              sx={{
                                py: 1.5,
                                px: 4,
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                fontSize: '1rem',
                                minWidth: 200,
                              }}
                            >
                              {loading ? (
                                <CircularProgress size={24} color='inherit' />
                              ) : (
                                'Enviar Feedback'
                              )}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}

                    {currentTipo === 'questionario' &&
                      (aulaAtual.concluido ? (
                        <Box
                          sx={{
                            textAlign: 'center',
                            py: 8,
                            px: 4,
                            bgcolor: 'white',
                            borderRadius: '16px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                            border: '1px solid',
                            borderColor: 'success.light',
                          }}
                        >
                          <CheckCircle
                            sx={{ fontSize: 80, color: 'success.main', mb: 3 }}
                          />
                          <Typography
                            variant='h4'
                            fontWeight='900'
                            gutterBottom
                            sx={{ color: '#1a237e' }}
                          >
                            Parabéns por completar o curso!
                          </Typography>
                          <Typography
                            variant='body1'
                            color='text.secondary'
                            sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
                          >
                            Suas respostas foram enviadas com sucesso e você
                            concluiu esta etapa. Continue sua jornada de
                            aprendizado!
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              gap: 2,
                              justifyContent: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            {progressoTotal === 100 && (
                              <>
                                <Button
                                  variant='contained'
                                  color='success'
                                  startIcon={<WorkspacePremium />}
                                  onClick={handleDownloadCertificate}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    px: 4,
                                    py: 1.5,
                                  }}
                                >
                                  Download Certificado
                                </Button>
                                <Button
                                  variant='contained'
                                  startIcon={<LinkedIn />}
                                  onClick={handleShareLinkedIn}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    bgcolor: '#0077b5',
                                    boxShadow:
                                      '0 4px 12px rgba(0, 119, 181, 0.2)',
                                    px: 4,
                                    py: 1.5,
                                    '&:hover': {
                                      bgcolor: '#005582',
                                    },
                                  }}
                                >
                                  Compartilhar no LinkedIn
                                </Button>
                              </>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Questionario
                          contentId={aulaAtual.id_curso_conteudo_usuario}
                          onComplete={handleMarcarConcluida}
                        />
                      ))}

                    {currentTipo === 'simulador' && (
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          maxWidth: '1200px',
                          mx: 'auto',
                          aspectRatio: '16/9',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          bgcolor: '#f8fafc',
                          borderRadius: '12px',
                          border: '1px solid',
                          borderColor: 'divider',
                          p: showSimulatorIframe ? 0 : 4,
                          textAlign: 'center',
                          mb: 4,
                          overflow: 'hidden',
                        }}
                      >
                        {showSimulatorIframe ? (
                          <iframe
                            src={simulatorUrl}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              borderRadius: '12px',
                            }}
                            title='Simulador Prático'
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                            allowFullScreen
                          />
                        ) : (
                          <>
                            <SportsEsports
                              sx={{
                                fontSize: 64,
                                color: 'primary.main',
                                mb: 2,
                              }}
                            />
                            <Typography
                              variant='h5'
                              fontWeight='bold'
                              gutterBottom
                            >
                              Simulador Prático
                            </Typography>
                            <Typography
                              variant='body1'
                              color='text.secondary'
                              sx={{ mb: 4, maxWidth: 600 }}
                            >
                              Acesse o simulador interativo para realizar sua
                              atividade prática.
                            </Typography>

                            {aulaAtual.link ? (
                              <Button
                                variant='contained'
                                size='large'
                                startIcon={<Launch />}
                                onClick={() => {
                                  const max = aulaAtual.tentativas_max || 3;
                                  const remaining =
                                    aulaAtual.tentativas_restantes !== undefined
                                      ? aulaAtual.tentativas_restantes
                                      : max;
                                  const isUnlimited =
                                    aulaAtual.simulador === true &&
                                    aulaAtual.atividade === true;
                                  requestConsent(
                                    async () => {
                                      // If id_curso_conteudo is already known, use it directly
                                      let conteudoId =
                                        aulaAtual.id_curso_conteudo;

                                      if (!conteudoId) {
                                        // User requested approach: Get contents of this module to find the content template ID
                                        try {
                                          if (!aulaAtual.concluido) {
                                            await startContent(
                                              aulaAtual.id_curso_conteudo_usuario
                                            );
                                          }

                                          // Fetch User Courses to find the course ID and version
                                          const userCourses =
                                            await getUserCourses();
                                          const currentUserCourse =
                                            userCourses?.cursos_usuario?.find(
                                              c =>
                                                String(c.id_curso_usuario) ===
                                                String(id)
                                            ) ||
                                            userCourses?.find?.(
                                              c =>
                                                String(c.id_curso_usuario) ===
                                                String(id)
                                            );

                                          let cursoVersaoId =
                                            currentUserCourse?.id_curso_versao ||
                                            currentUserCourse?.curso
                                              ?.id_curso_versao;

                                          // Fallback to fetch from public catalog if we only have id_curso
                                          if (
                                            !cursoVersaoId &&
                                            currentUserCourse?.id_curso
                                          ) {
                                            const allCourses =
                                              await getCourses();
                                            const templateCourse =
                                              allCourses?.cursos?.find(
                                                c =>
                                                  String(c.id_curso) ===
                                                  String(
                                                    currentUserCourse.id_curso
                                                  )
                                              );
                                            if (
                                              templateCourse &&
                                              templateCourse.versoes &&
                                              templateCourse.versoes.length > 0
                                            ) {
                                              cursoVersaoId =
                                                templateCourse.versoes[0]
                                                  .id_curso_versao;
                                            }
                                          }

                                          console.log(
                                            'Found cursoVersaoId:',
                                            cursoVersaoId
                                          );
                                          if (cursoVersaoId) {
                                            // Call /api/v1/curso-modulo/ with curso_versao_id as requested
                                            const modRes =
                                              await getCourseModulesByVersion(
                                                cursoVersaoId
                                              );
                                            const modItems = Array.isArray(
                                              modRes
                                            )
                                              ? modRes
                                              : modRes.items ||
                                                modRes.modulos ||
                                                [];

                                            // Match the template module
                                            const templateMod = modItems.find(
                                              m =>
                                                m.nm_titulo ===
                                                  moduloAtual.modulo_titulo ||
                                                m.nu_ordem === moduloAtual.ordem
                                            );
                                            const moduloId =
                                              templateMod?.id_curso_modulo;

                                            console.log(
                                              'Found moduloId from /curso-modulo/:',
                                              moduloId
                                            );

                                            if (moduloId) {
                                              // Call /api/v1/curso-conteudo/ with modulo_id
                                              const res =
                                                await getCourseContentsByModule(
                                                  moduloId
                                                );
                                              const contentsList =
                                                res.conteudos ||
                                                (Array.isArray(res)
                                                  ? res
                                                  : res.items || []);

                                              console.log(
                                                'Found contentsList:',
                                                contentsList
                                              );
                                              // Find the template content matching the current lesson's title
                                              const match = contentsList.find(
                                                c =>
                                                  // Match by title assuming nm_titulo from template maps to titulo in user data
                                                  (c.nm_titulo ===
                                                    aulaAtual.titulo ||
                                                    c.nm_titulo ===
                                                      aulaAtual.nome) &&
                                                  // Only map simulators
                                                  (c.id_tipo_conteudo === 3 ||
                                                    aulaAtual.simulador ===
                                                      true)
                                              );

                                              if (match) {
                                                // Extract from the exact field in the JSON response
                                                conteudoId =
                                                  match.id_curso_conteudo;
                                                console.log(
                                                  'Found conteudoId in template:',
                                                  conteudoId
                                                );
                                              }
                                            }
                                          }

                                          // Fallback (just in case the match fails)
                                          if (!conteudoId) {
                                            const trilha =
                                              await getTrilhaAcompanhamento(id);
                                            const path =
                                              trilha?.conteudo_path || [];
                                            const match = path.find(
                                              c =>
                                                c.id_curso_conteudo_usuario ===
                                                aulaAtual.id_curso_conteudo_usuario
                                            );
                                            conteudoId =
                                              match?.id_curso_conteudo;
                                          }
                                        } catch (err) {
                                          console.error(
                                            '[CursoConteudoPage] Falha ao buscar id_curso_conteudo para simulador usando endpoint sugerido:',
                                            err
                                          );
                                        }
                                      }

                                      if (!conteudoId) {
                                        console.error(
                                          '[CursoConteudoPage] conteudo_id ainda indisponível para simulador:',
                                          aulaAtual.id_curso_conteudo_usuario
                                        );
                                        return;
                                      }

                                      const url = `${aulaAtual.link}${
                                        aulaAtual.link.includes('?') ? '&' : '?'
                                      }user_id=${
                                        getCurrentUser()?.id_usuario
                                      }&id_curso_conteudo_usuario=${
                                        aulaAtual.id_curso_conteudo_usuario
                                      }&conteudo_id=${
                                        conteudoId
                                      }&curso_usuario_id=${id}`;
                                      setSimulatorUrl(url);
                                      setShowSimulatorIframe(true);

                                      if (
                                        conteudoId &&
                                        !aulaAtual.id_curso_conteudo
                                      ) {
                                        patchConteudoNoOutline(
                                          aulaAtual.id_curso_conteudo_usuario,
                                          { id_curso_conteudo: conteudoId }
                                        );
                                      }
                                    },
                                    max,
                                    remaining,
                                    true, // isSimulator
                                    isUnlimited
                                  );
                                }}
                                sx={{
                                  px: 6,
                                  py: 2,
                                  borderRadius: '12px',
                                  textTransform: 'none',
                                  fontWeight: 'bold',
                                  fontSize: '1.1rem',
                                }}
                              >
                                Abrir Simulador
                              </Button>
                            ) : (
                              <Alert severity='warning' sx={{ width: '100%' }}>
                                A URL para esta atividade ainda não foi
                                configurada.
                              </Alert>
                            )}
                          </>
                        )}
                      </Box>
                    )}

                    {/* Tabs: Descrição + Materiais de Apoio */}
                    {currentTipo !== 'avaliacao' &&
                      currentTipo !== 'feedback' &&
                      currentTipo !== 'questionario' && (
                        <Box sx={{ mb: 4, mt: 4 }}>
                          <Tabs
                            value={videoTabIndex}
                            onChange={(e, newVal) => setVideoTabIndex(newVal)}
                            sx={{
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              mb: 3,
                              '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 'bold',
                                fontSize: '0.95rem',
                              },
                            }}
                          >
                            <Tab label='Descrição' />
                            <Tab label='Materiais de Apoio' />
                          </Tabs>

                          {videoTabIndex === 0 && (
                            <Box>
                              <Typography
                                variant='body1'
                                color='text.secondary'
                              >
                                {aulaAtual.descricao ||
                                  (currentTipo === 'simulador' ||
                                  currentTipo === 'atividade_teorica' ||
                                  currentTipo === 'atividade'
                                    ? 'Nenhuma descrição disponível para esta atividade.'
                                    : currentTipo === 'avaliacao'
                                      ? 'Nenhuma descrição disponível para esta avaliação.'
                                      : currentTipo === 'feedback'
                                        ? 'Nenhuma descrição sugerida.'
                                        : 'Nenhuma descrição disponível para esta aula.')}
                              </Typography>
                              {isDevMode &&
                                aulaAtual.tipo !== 'avaliacao' &&
                                currentTipo !== 'feedback' &&
                                currentTipo !== 'questionario' &&
                                (currentTipo !== 'video' ||
                                  aulaAtual.concluido ||
                                  videoCompleted) && (
                                  <Button
                                    variant={
                                      aulaAtual.concluido
                                        ? 'outlined'
                                        : 'contained'
                                    }
                                    color={
                                      aulaAtual.concluido
                                        ? 'success'
                                        : 'primary'
                                    }
                                    size='large'
                                    onClick={handleMarcarConcluida}
                                    startIcon={
                                      aulaAtual.concluido ? (
                                        <CheckCircle />
                                      ) : (
                                        <CheckCircleOutline />
                                      )
                                    }
                                    sx={{
                                      borderRadius: '8px',
                                      px: 4,
                                      height: 56,
                                      textTransform: 'none',
                                      fontWeight: 'bold',
                                      minWidth: 200,
                                      mt: 3,
                                    }}
                                  >
                                    {aulaAtual.concluido
                                      ? 'Concluída'
                                      : 'Marcar como concluída'}
                                  </Button>
                                )}
                            </Box>
                          )}

                          {videoTabIndex === 1 && (
                            <Box>
                              {aulaAtual.materiais_auxiliares &&
                              aulaAtual.materiais_auxiliares.length > 0 ? (
                                <Grid container spacing={2}>
                                  {aulaAtual.materiais_auxiliares.map(
                                    (mat, idx) => {
                                      const isPdf =
                                        (mat.formato &&
                                          mat.formato.toLowerCase() ===
                                            'pdf') ||
                                        (mat.url_download &&
                                          mat.url_download
                                            .toLowerCase()
                                            .endsWith('.pdf'));
                                      return (
                                        <Grid item xs={12} sm={6} key={idx}>
                                          <Card
                                            component='a'
                                            href={mat.url_download}
                                            target={
                                              isPdf ? '_blank' : undefined
                                            }
                                            rel={
                                              isPdf
                                                ? 'noopener noreferrer'
                                                : undefined
                                            }
                                            download={isPdf ? undefined : true}
                                            variant='outlined'
                                            sx={{
                                              textDecoration: 'none',
                                              color: 'inherit',
                                              display: 'block',
                                              borderRadius: '12px',
                                              transition: 'all 0.2s',
                                              '&:hover': {
                                                borderColor: 'primary.main',
                                                bgcolor: '#f8fafc',
                                              },
                                            }}
                                          >
                                            <CardContent
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: '16px !important',
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  p: 1.5,
                                                  bgcolor: '#eff6ff',
                                                  borderRadius: '8px',
                                                  color: 'primary.main',
                                                  mr: 2,
                                                }}
                                              >
                                                <Description />
                                              </Box>
                                              <Box
                                                sx={{
                                                  flexGrow: 1,
                                                  mr: 2,
                                                  minWidth: 0,
                                                }}
                                              >
                                                <Typography
                                                  variant='body2'
                                                  fontWeight='bold'
                                                  noWrap
                                                >
                                                  {mat.nome}
                                                </Typography>
                                                <Typography
                                                  variant='caption'
                                                  color='text.secondary'
                                                >
                                                  {mat.tamanho
                                                    ? `${(
                                                        mat.tamanho / 1024
                                                      ).toFixed(1)} KB • `
                                                    : ''}
                                                  {mat.formato}
                                                </Typography>
                                              </Box>
                                              <Typography
                                                variant='button'
                                                sx={{
                                                  fontSize: '0.8125rem',
                                                  fontWeight: 'bold',
                                                  color: 'primary.main',
                                                  textTransform: 'none',
                                                  ml: 'auto',
                                                  whiteSpace: 'nowrap',
                                                }}
                                              >
                                                {isPdf
                                                  ? 'Visualizar'
                                                  : 'Baixar'}
                                              </Typography>
                                            </CardContent>
                                          </Card>
                                        </Grid>
                                      );
                                    }
                                  )}
                                </Grid>
                              ) : (
                                <Typography
                                  variant='body1'
                                  color='text.secondary'
                                >
                                  Nenhum material de apoio disponível para este
                                  conteúdo.
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      )}
                  </>
                </>
              )
            )}
          </Box>
        </Box>

        {/* Bottom Navigation */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            px: { xs: 2, md: 5 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'rgba(250, 250, 250, 0.85)',
            backdropFilter: 'blur(12px)',
            borderTop: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 -4px 24px rgba(0,0,0,0.02)',
            zIndex: 10,
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            disabled={moduloAtualIdx === 0 && aulaAtualIdx === 0}
            onClick={handleAulaAnterior}
            sx={{
              fontWeight: '700',
              textTransform: 'none',
              color: '#4A5568',
              px: { xs: 2, md: 3 },
              py: 1,
              borderRadius: '12px',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: '#F0F4F8', color: '#0A2540' },
              '&.Mui-disabled': { color: 'rgba(0,0,0,0.26)' },
            }}
          >
            Conteúdo Anterior
          </Button>

          <Box
            sx={{ textAlign: 'center', display: { xs: 'none', sm: 'block' } }}
          >
            <Typography
              variant='caption'
              sx={{ color: '#6B778C', fontWeight: 600, letterSpacing: '0.5px' }}
            >
              CONTEÚDO {aulaAtualIdx + 1} DE{' '}
              {moduloAtual?.conteudos?.length || 0} DO MÓDULO{' '}
              {moduloAtual?.ordem}
            </Typography>
          </Box>

          <Button
            endIcon={<PlayArrow />}
            disabled={
              (moduloAtualIdx === curso?.modulos_conteudos?.length - 1 &&
                aulaAtualIdx === moduloAtual?.conteudos?.length - 1) ||
              (aulaAtual?.obrigatorio && !aulaAtual?.concluido)
            }
            onClick={() => handleProximaAula()}
            variant='contained'
            sx={{
              px: { xs: 3, md: 4 },
              py: 1.5,
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #0A2540 0%, #173855 100%)',
              color: '#FFF',
              boxShadow: '0 8px 20px rgba(10, 37, 64, 0.15)',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #0F282D 0%, #1A4060 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 24px rgba(10, 37, 64, 0.2)',
              },
              '&.Mui-disabled': {
                background: '#EAEAEA',
                color: 'rgba(0,0,0,0.26)',
                boxShadow: 'none',
                transform: 'none',
              },
            }}
          >
            Próximo Conteúdo
          </Button>
        </Box>
      </Box>
      <ConsentDialog
        open={consentOpen}
        onClose={() => setConsentOpen(false)}
        onConfirm={handleConsentConfirm}
        maxTries={consentData.max}
        remainingTries={consentData.remaining}
        isSimulator={consentData.isSimulator}
        isUnlimited={consentData.isUnlimited}
      />

      {/* Confirm Submit Avaliacao Dialog */}
      <Dialog
        open={confirmSubmitAvaliacaoOpen}
        onClose={() => setConfirmSubmitAvaliacaoOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant='h6' fontWeight='bold' sx={{ mb: 2 }}>
            Confirmar Envio
          </Typography>
          <Typography variant='body1' sx={{ color: 'text.secondary', mb: 3 }}>
            Tem certeza que deseja enviar suas respostas? Você não poderá
            alterá-las após o envio.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant='outlined'
              onClick={() => setConfirmSubmitAvaliacaoOpen(false)}
              sx={{
                fontWeight: 'bold',
                textTransform: 'none',
                borderRadius: '8px',
              }}
            >
              Cancelar
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={() => {
                setConfirmSubmitAvaliacaoOpen(false);
                handleEnviarAvaliacao();
              }}
              sx={{
                fontWeight: 'bold',
                textTransform: 'none',
                borderRadius: '8px',
              }}
            >
              Enviar Respostas
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Certificate Preview Dialog */}
      <Dialog
        open={openCertDialog}
        onClose={handleCloseCertDialog}
        maxWidth='lg'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            width: '90vw',
            maxWidth: '1200px',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant='h5' fontWeight='bold' sx={{ color: '#0A2540' }}>
            Visualização do Certificado
          </Typography>
          <IconButton
            onClick={handleCloseCertDialog}
            sx={{
              color: 'text.secondary',
              bgcolor: '#F0F4F8',
              '&:hover': { bgcolor: '#E2E8F0' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            p: { xs: 2, md: 4 },
            bgcolor: '#F0F4F8',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              aspectRatio: { xs: 'auto', md: '297 / 210' },
              height: { xs: '70vh', md: 'auto' },
              position: 'relative',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: '#FFFFFF',
            }}
          >
            {certLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress size={60} />
              </Box>
            ) : certUrl ? (
              <iframe
                src={`${certUrl}#toolbar=0&navpanes=0&scrollbar=0&view=Fit`}
                title='Certificado'
                width='100%'
                height='100%'
                style={{ border: 'none' }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography color='error' fontWeight='bold'>
                  Não foi possível carregar o certificado.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default CursoConteudoPage;
